import * as THREE from 'three';
import { GameState } from './GameState';
import { CooldownManager, SpellType } from './CooldownManager';
import { Arena } from '../scene/Arena';
import { ArenaThemeManager, ArenaThemeId } from '../scene/ArenaThemeManager';
import { GestureDetector } from '../gestures/GestureDetector';
import { UIManager } from '../ui/UIManager';
import { AudioManager } from '../audio/AudioManager';
import { CPUOpponent } from '../wizards/CPUOpponent';
import { Fireball } from '../spells/Fireball';
import { IceShard } from '../spells/IceShard';
import { Shield } from '../spells/Shield';
import { LightningBolt } from '../spells/LightningBolt';
import { HealSpell } from '../spells/HealSpell';
import { Spell } from '../spells/Spell';
import { ReflectBarrier } from '../spells/ReflectBarrier';
import { TeleportSpell } from '../spells/TeleportSpell';
import { MeteorSpell } from '../spells/MeteorSpell';
import { SuperShield } from '../spells/SuperShield';
import { BlizzardSpell } from '../spells/BlizzardSpell';
import { SpellCollisionSystem } from '../spells/SpellCollisionSystem';
import { WizardType, WIZARD_CONFIGS } from '../wizards/WizardTypes';
import { WizardModel } from '../models/WizardModel';
import { MultiplayerClient } from '../multiplayer/MultiplayerClient';
import { PlayerRole, CheerType, EmoteType } from '../multiplayer/protocol';
import { getTTSService, GAME_PHRASES, TTSService } from '../audio/TTSService';
import { EmoteDisplay, EMOTE_TTS_TEXT } from '../social/EmoteDisplay';
import { FriendClient } from '../social/FriendClient';
import { modelLoader } from '../utils/ModelLoader';
import { GestureInfo } from '../gestures/GestureDetector';
import { PostProcessing } from '../effects/PostProcessing';
import { EnvironmentalEffects } from '../effects/EnvironmentalEffects';
import { SpellEffectsManager } from '../effects/SpellEffectsManager';
import { KeyboardInput, loadKeyboardPreference } from '../input/KeyboardInput';
import { GyroscopeInput } from '../gestures/GyroscopeInput';
import { AchievementManager } from '../progression/AchievementManager';
import { CosmeticManager } from '../progression/CosmeticManager';
import { DustManager } from '../economy/DustManager';
import { EconomyUI } from '../economy/EconomyUI';

export class Game {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private clock: THREE.Clock;

  private gameState: GameState;
  private cooldownManager: CooldownManager;
  private cpuCooldownManager: CooldownManager;
  private arena: Arena | null = null;
  private arenaThemeManager: ArenaThemeManager;
  private gestureDetector: GestureDetector;
  private uiManager: UIManager;
  private audioManager: AudioManager;
  private ttsService: TTSService;
  private cpuOpponent: CPUOpponent;
  private keyboardInput: KeyboardInput;

  private activeSpells: Set<Spell> = new Set();
  private playerShield: Shield | null = null;
  private enemyShield: Shield | null = null;
  private playerReflect: ReflectBarrier | null = null;
  private enemyReflect: ReflectBarrier | null = null;
  private playerSuperShield: SuperShield | null = null;
  private enemySuperShield: SuperShield | null = null;
  private spellCollisionSystem: SpellCollisionSystem | null = null;
  private playerInvulnerable: boolean = false;
  private playerInvulnerableEndTime: number = 0;

  // Mobile gyroscope input
  private gyroscopeInput: GyroscopeInput | null = null;

  // Lazy camera initialization
  private cameraInitialized: boolean = false;
  private cameraInitPromise: Promise<void> | null = null;

  // Practice mode for How-to-Play (shows gesture feedback without casting)
  private practiceMode: boolean = false;

  // Helper to properly dispose Three.js objects and avoid memory leaks
  private disposeObject3D(object: THREE.Object3D): void {
    object.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.geometry) {
          child.geometry.dispose();
        }
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => mat.dispose());
          } else {
            child.material.dispose();
          }
        }
      }
      if (child instanceof THREE.Sprite) {
        if (child.material) {
          if (child.material.map) {
            child.material.map.dispose();
          }
          child.material.dispose();
        }
      }
      if (child instanceof THREE.Light) {
        child.dispose?.();
      }
    });
  }

  private playerPosition: THREE.Vector3 = new THREE.Vector3(0, 1, 8);
  private enemyPosition: THREE.Vector3 = new THREE.Vector3(0, 1, -8);

  private lastPositionSendTime: number = 0;
  private readonly POSITION_SEND_INTERVAL: number = 100;
  private remoteTargetPosition: THREE.Vector3 | null = null;
  private remoteInterpolationSpeed: number = 10;

  // Wizard visual references
  private playerHat: THREE.Group | null = null;
  private enemyHat: THREE.Group | null = null;
  private playerWizardModel: WizardModel | null = null;
  private enemyWizardModel: WizardModel | null = null;

  private isRunning: boolean = false;
  private screenShakeIntensity: number = 0;

  // Camera rotation
  private cameraAngle: number = 0;
  private cameraRadius: number = 15;
  private cameraHeight: number = 8;
  private cameraRotationSpeed: number = 0.15; // degrees per frame

  // Wizard name labels
  private playerNameLabel: THREE.Sprite | null = null;
  private enemyNameLabel: THREE.Sprite | null = null;

  // Model type preference
  private useMeshyModels: boolean = false; // Default to procedural

  // Spell casting restrictions
  private playerHasActiveProjectile: boolean = false;

  // Enemy casting telegraph system
  private pendingEnemySpell: SpellType | null = null;
  private pendingEnemySpellTime: number = 0;
  private enemyCastWarning: THREE.Group | null = null;
  private static readonly ENEMY_CAST_DELAY = 2000; // 2 seconds warning before cast

  // Visual effects systems
  private postProcessing: PostProcessing | null = null;
  private environmentalEffects: EnvironmentalEffects | null = null;
  private spellEffectsManager: SpellEffectsManager | null = null;

  // Progression systems
  private achievementManager: AchievementManager;
  private cosmeticManager: CosmeticManager;

  // Economy systems
  private dustManager: DustManager;
  private economyUI: EconomyUI | null = null;

  // Multiplayer properties
  private isMultiplayer: boolean = false;
  private multiplayerClient: MultiplayerClient | null = null;
  private myRole: PlayerRole | null = null;
  private currentRoomId: string = '';

  // Social features
  private emoteDisplay: EmoteDisplay | null = null;
  private friendClient: FriendClient | null = null;
  private lastEmoteTime: number = 0;

  // --- Unique wizard mechanic state ---
  // Necromancer: DoT tracking
  private playerDotActive: boolean = false;
  private playerDotEndTime: number = 0;
  private playerDotDps: number = 3;
  private enemyDotActive: boolean = false;
  private enemyDotEndTime: number = 0;
  private enemyDotDps: number = 3;
  // Druid: healing zone tracking
  private playerHealZoneActive: boolean = false;
  private playerHealZoneEndTime: number = 0;
  private playerHealZoneHps: number = 2;
  private enemyHealZoneActive: boolean = false;
  private enemyHealZoneEndTime: number = 0;
  private enemyHealZoneHps: number = 2;
  private playerHealZoneVisual: THREE.Mesh | null = null;
  private enemyHealZoneVisual: THREE.Mesh | null = null;
  // Druid: root tracking
  private playerRooted: boolean = false;
  private playerRootEndTime: number = 0;
  private enemyRooted: boolean = false;
  private enemyRootEndTime: number = 0;
  // Chronomancer: cooldown reset tracking
  private playerRecentCasts: number[] = [];
  private enemyRecentCasts: number[] = [];
  private playerNextSpellFree: boolean = false;
  private enemyNextSpellFree: boolean = false;
  // Chronomancer: time echo - absorbs one incoming hit after teleport
  private playerTimeEchoActive: boolean = false;
  private enemyTimeEchoActive: boolean = false;

  constructor() {
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 100);
    this.renderer = new THREE.WebGLRenderer({
      canvas: document.getElementById('game-canvas') as HTMLCanvasElement,
      antialias: true
    });
    this.clock = new THREE.Clock();

    this.gameState = new GameState();
    this.cooldownManager = new CooldownManager();
    this.cpuCooldownManager = new CooldownManager();
    this.gestureDetector = new GestureDetector();
    this.uiManager = new UIManager(this.gameState, this.cooldownManager);
    this.audioManager = new AudioManager();
    this.ttsService = getTTSService();
    this.cpuOpponent = new CPUOpponent('archmage');
    this.cpuOpponent.setCooldownManager(this.cpuCooldownManager);
    this.keyboardInput = new KeyboardInput();
    this.achievementManager = new AchievementManager();
    this.cosmeticManager = new CosmeticManager(this.achievementManager);
    this.dustManager = new DustManager();
    this.arenaThemeManager = new ArenaThemeManager();

    // Achievement unlock notification
    this.achievementManager.onUnlock((achievement) => {
      this.audioManager.play('achievement_unlock');
      this.uiManager.showAchievementToast(achievement.name, achievement.description);
      // Check if any cosmetics were newly unlocked
      this.cosmeticManager.checkAllUnlockConditions();
    });

    this.setupRenderer();
    this.setupCamera();
    this.setupEventListeners();
  }

  private setupRenderer(): void {
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;
  }

  private setupCamera(): void {
    this.camera.position.set(0, 8, 15);
    this.camera.lookAt(0, 0, 0);
  }

  private setupEventListeners(): void {
    window.addEventListener('resize', this.onWindowResize.bind(this));

    // Toggle FPS counter with F3 key and movement keys
    window.addEventListener('keydown', (e) => {
      if (e.key === 'F3') {
        e.preventDefault();
        this.toggleFpsCounter();
      }
    });

    this.uiManager.setCallbacks({
      onStartGame: () => this.startGame(),
      onWizardSelect: (type) => this.selectWizard(type),
      onPlayAgain: () => this.playAgain(),
      onMainMenu: () => this.returnToMenu(),
      onInitCamera: () => this.initCameraIfNeeded(),
      onSetPracticeMode: (enabled) => this.setPracticeMode(enabled),
      onGestureConfirmSound: () => this.audioManager.playGestureConfirm(),
      onShowAchievements: () => this.uiManager.showAchievementGallery(this.achievementManager),
      onShowCosmetics: () => this.uiManager.showCosmeticSelector(this.cosmeticManager),
      onShowFriends: () => {
        if (this.friendClient) {
          this.uiManager.showFriendList(this.friendClient);
        }
      },
      onShowShop: () => this.showShop(),
      onArenaThemeChange: (themeId: ArenaThemeId) => {
        this.arenaThemeManager.saveTheme(themeId);
        if (this.arena) {
          this.arena.applyCurrentTheme();
        }
      },
    });

    // Set up emote callback
    this.uiManager.setEmoteCallback((type: EmoteType) => {
      this.sendEmote(type);
    });
  }

  private toggleFpsCounter(): void {
    this.showFps = !this.showFps;
    if (this.showFps) {
      this.createFpsCounter();
    } else if (this.fpsCounter) {
      this.fpsCounter.remove();
      this.fpsCounter = null;
    }
  }

  private createFpsCounter(): void {
    if (this.fpsCounter) return;

    this.fpsCounter = document.createElement('div');
    this.fpsCounter.id = 'fps-counter';
    this.fpsCounter.style.cssText = `
      position: fixed; top: 10px; left: 10px; z-index: 9999;
      background: rgba(0,0,0,0.7); color: #44FF44; padding: 8px 12px;
      font-family: monospace; font-size: 14px; border-radius: 4px;
      pointer-events: none;
    `;
    document.body.appendChild(this.fpsCounter);
  }

  private updateFpsCounter(): void {
    if (!this.showFps) return;

    this.frameCount++;
    const now = performance.now();

    if (now - this.lastFpsUpdate >= 1000) {
      this.currentFps = Math.round(this.frameCount * 1000 / (now - this.lastFpsUpdate));
      this.frameCount = 0;
      this.lastFpsUpdate = now;

      if (this.fpsCounter) {
        const memory = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
        const memoryMB = memory ? Math.round(memory.usedJSHeapSize / 1048576) : 0;
        this.fpsCounter.innerHTML = `FPS: ${this.currentFps}<br>Spells: ${this.activeSpells.size}${memory ? `<br>Memory: ${memoryMB}MB` : ''}`;
      }
    }
  }

  async init(roomId?: string): Promise<void> {
    try {
      // Show menu immediately with fade-in (no loading screen)
      this.uiManager.showScreen('menu');

      // Initialize audio in background
      this.audioManager.init().catch(err => console.warn('Audio init warning:', err));

      // Preload TTS phrases for smoother gameplay
      this.ttsService.preloadAll([
        GAME_PHRASES.WELCOME,
        GAME_PHRASES.WIZARD_DUEL,
        GAME_PHRASES.FIGHT,
        GAME_PHRASES.GAME_OVER,
        GAME_PHRASES.VICTORY,
        GAME_PHRASES.DEFEAT,
        GAME_PHRASES.FIREBALL,
        GAME_PHRASES.ICE_SHARD,
        GAME_PHRASES.SHIELD,
        'Well Played',
        'Nice Combo',
        'Good Game',
        'Wow',
      ]).catch(err => console.warn('TTS preload warning:', err));

      this.arena = new Arena(this.scene, this.arenaThemeManager);

      // Initialize visual effects systems
      this.postProcessing = new PostProcessing(this.renderer, this.scene, this.camera);
      this.environmentalEffects = new EnvironmentalEffects(this.scene);
      this.spellEffectsManager = new SpellEffectsManager(this.scene);
      this.spellCollisionSystem = new SpellCollisionSystem(this.scene);

      // Initialize emote display
      this.emoteDisplay = new EmoteDisplay(this.scene);

      // Initialize friend system
      const playerId = this.getOrCreatePlayerId();
      const playerName = localStorage.getItem('wizard_name') || 'Wizard';
      this.friendClient = new FriendClient(playerId, playerName);
      this.friendClient.register().catch(err => console.warn('Friend registration warning:', err));
      this.friendClient.startHeartbeat();

      // Preload 3D models
      console.log('Preloading 3D models...');
      await modelLoader.preloadAll().catch(err => console.warn('Model preload warning:', err));

      await this.createWizardVisuals();

      // Always create clickable spell buttons (work on all devices)
      this.createSpellButtons();

      // Gesture detection is now lazily initialized when needed
      // (when gameplay starts or How to Play is shown)
      console.log('Device detection:', {
        isMobile: this.isMobileDevice(),
        userAgent: navigator.userAgent.substring(0, 50),
        touchPoints: navigator.maxTouchPoints,
        hasPointerFine: window.matchMedia('(pointer: fine)').matches,
        windowWidth: window.innerWidth
      });

      this.gameLoop();

      // Initialize multiplayer if roomId provided
      if (roomId) {
        this.isMultiplayer = true;
        await this.initMultiplayer(roomId);
      } else {
        // Menu is already shown, just start music and welcome
        this.audioManager.playMusic();
        this.ttsService.speak(GAME_PHRASES.WELCOME);
      }

    } catch (error) {
      console.error('Game initialization failed:', error);
      throw error;
    }
  }

  private async initMultiplayer(roomId: string): Promise<void> {
    this.multiplayerClient = new MultiplayerClient();
    this.currentRoomId = roomId;

    // Set up event handlers
    this.multiplayerClient.on('joined', ({ role, roomState }) => {
      this.myRole = role;
      console.log(`Joined room ${roomId} as ${role}`);

      if (role === 'spectator') {
        this.uiManager.showSpectatorMode(roomState);
      } else {
        // Show multiplayer lobby
        this.uiManager.showMultiplayerLobby(role, roomState, roomId);
      }
      this.audioManager.playMusic();
    });

    this.multiplayerClient.on('playerJoined', ({ role, name, roomState }) => {
      this.uiManager.showPlayerJoined(role, name);
      // Refresh the lobby UI to show wizard selection now that opponent has joined
      if (this.myRole && this.myRole !== 'spectator') {
        this.uiManager.showMultiplayerLobby(this.myRole, roomState, this.currentRoomId);
      }
    });

    this.multiplayerClient.on('wizardSelected', ({ player, wizardType }) => {
      this.uiManager.updateWizardSelection(player, wizardType);
    });

    this.multiplayerClient.on('playerReady', ({ player }) => {
      this.uiManager.updatePlayerReady(player);
    });

    this.multiplayerClient.on('gameStart', ({ gameState }) => {
      this.handleMultiplayerGameStart(gameState);
    });

    this.multiplayerClient.on('spellCast', ({ caster, spell }) => {
      this.handleRemoteSpellCast(caster, spell);
    });

    this.multiplayerClient.on('damage', ({ target, amount, newHealth, shieldAbsorbed }) => {
      this.handleRemoteDamage(target, amount, newHealth, shieldAbsorbed);
    });

    this.multiplayerClient.on('slowApplied', ({ target, duration, multiplier }) => {
      this.handleSlowApplied(target, duration, multiplier);
    });

    this.multiplayerClient.on('positionUpdate', ({ player, x, z }) => {
      this.handleRemotePositionUpdate(player, x, z);
    });

    this.multiplayerClient.on('gameOver', ({ winner }) => {
      this.handleMultiplayerGameOver(winner);
    });

    this.multiplayerClient.on('cheer', ({ from, cheerType }) => {
      this.handleCheer(from, cheerType);
    });

    this.multiplayerClient.on('emote', ({ from, emoteType, player }) => {
      this.handleEmote(from, emoteType, player);
    });

    this.multiplayerClient.on('spectatorCount', ({ count }) => {
      this.uiManager.updateSpectatorCount(count);
    });

    this.multiplayerClient.on('playerDisconnected', ({ player }) => {
      this.uiManager.showPlayerDisconnected(player);
    });

    this.multiplayerClient.on('error', ({ message }) => {
      console.error('Multiplayer error:', message);
    });

    // Initialize economy UI for multiplayer
    const uiOverlay = document.getElementById('ui-overlay');
    if (uiOverlay) {
      this.economyUI = new EconomyUI(uiOverlay, this.multiplayerClient, this.dustManager);
      this.economyUI.createDustDisplay();
    }

    // Unlock cosmetics on successful purchase
    this.multiplayerClient.on('purchaseResult', ({ success, cosmeticId }) => {
      if (success) {
        this.cosmeticManager.unlockCosmetic(cosmeticId);
      }
    });

    // Show betting panel for spectators when game is in lobby
    this.multiplayerClient.on('joined', ({ role, roomState }) => {
      if (role === 'spectator' && !roomState.gameStarted && this.economyUI) {
        if (roomState.player1 && roomState.player2) {
          const p1Name = roomState.player1.name || 'Player 1';
          const p2Name = roomState.player2.name || 'Player 2';
          this.economyUI.showBettingPanel(p1Name, p2Name);
        }
      }
    });

    // Show betting panel when both players have joined (for spectators already in room)
    this.multiplayerClient.on('playerJoined', ({ roomState }) => {
      if (this.myRole === 'spectator' && roomState.player1 && roomState.player2 && this.economyUI) {
        const p1Name = roomState.player1.name || 'Player 1';
        const p2Name = roomState.player2.name || 'Player 2';
        this.economyUI.showBettingPanel(p1Name, p2Name);
      }
    });

    // Hide betting panel when game starts
    this.multiplayerClient.on('gameStart', () => {
      if (this.economyUI) {
        this.economyUI.hideBettingPanel();
      }
    });

    // Connect to room
    const playerName = localStorage.getItem('wizard_name') || undefined;
    this.multiplayerClient.connect(roomId, playerName);

    // Update friend system with room info
    if (this.friendClient) {
      this.friendClient.updateRoomId(roomId);
    }
  }

  private async handleMultiplayerGameStart(gameState: { player1: { wizardType?: string; health: number }; player2: { wizardType?: string; health: number } }): Promise<void> {
    // Determine which player is us
    const isPlayer1 = this.myRole === 'player1';

    // Set wizard types
    const playerWizard = (isPlayer1 ? gameState.player1.wizardType : gameState.player2.wizardType) as WizardType || 'battlemage';
    const enemyWizard = (isPlayer1 ? gameState.player2.wizardType : gameState.player1.wizardType) as WizardType || 'battlemage';

    this.gameState.setPlayerWizard(playerWizard);
    this.gameState.setEnemyWizard(enemyWizard);
    this.gameState.startGame();

    // Update wizard visuals to match selected types
    await Promise.all([
      this.updateWizardAppearance(true, playerWizard),
      this.updateWizardAppearance(false, enemyWizard)
    ]);

    this.cooldownManager.reset();
    this.isRunning = true;
    this.uiManager.showScreen('playing');

    // Show emote panel for players during multiplayer matches
    if (this.myRole !== 'spectator') {
      this.uiManager.showEmotePanel();
    }

    // Show touch controls and enable keyboard for players (not spectators)
    if (this.myRole !== 'spectator') {
      this.showTouchControls(true);
      if (loadKeyboardPreference()) {
        this.keyboardInput.enable((spell) => this.onGestureDetected(spell));
      }
    }

    // Show spectator info if we're a spectator
    if (this.myRole === 'spectator') {
      this.uiManager.showSpectatorOverlay();
    }

    // Start battle music and theme ambient audio
    this.audioManager.playBattleMusic();
    this.arenaThemeManager.startAmbientAudio(0.3);

    // Announce game start with TTS
    this.ttsService.speak(GAME_PHRASES.WIZARD_DUEL);
    setTimeout(() => {
      this.ttsService.speak(GAME_PHRASES.FIGHT);
    }, 1500);
  }

  private handleRemoteSpellCast(caster: PlayerRole, spell: SpellType): void {
    // Determine if this is the local player's spell or remote
    const isLocalPlayer = caster === this.myRole;
    const isFromPlayer1 = caster === 'player1';

    // In multiplayer: player1 is at playerPosition, player2 is at enemyPosition
    // If we're player1: our spells go forward, enemy spells come toward us
    // If we're player2: our spells go backward (from enemy pos toward player pos)

    const isPlayer1 = this.myRole === 'player1';
    let startPos: THREE.Vector3;
    let targetPos: THREE.Vector3;

    if (isFromPlayer1) {
      // Player 1's spell: goes from player position to enemy position
      startPos = this.playerPosition.clone();
      targetPos = this.enemyPosition.clone();
    } else {
      // Player 2's spell: goes from enemy position to player position
      startPos = this.enemyPosition.clone();
      targetPos = this.playerPosition.clone();
    }

    startPos.y = 1.5;
    targetPos.y = 1.5;

    // The isPlayerSpell param determines visual direction
    // From our perspective, our spells are "player" spells
    const isOurSpell = (isPlayer1 && isFromPlayer1) || (!isPlayer1 && !isFromPlayer1);

    // Play sound (reflect and teleport use shield_cast as placeholder)
    const spellSoundMap: Record<string, string> = {
      reflect: 'shield_cast',
      teleport: 'shield_cast',
    };
    this.audioManager.play((spellSoundMap[spell] ?? `${spell}_cast`) as any);

    // Trigger casting animation on the caster's wizard model
    const casterModel = isOurSpell ? this.playerWizardModel : this.enemyWizardModel;
    casterModel?.triggerCast(spell as SpellType);

    // Create the spell visual
    switch (spell) {
      case 'fireball':
        const fireball = new Fireball(this.scene, startPos, targetPos, 0, isOurSpell);
        this.attachSpellEffects(fireball);
        this.activeSpells.add(fireball);
        break;
      case 'iceShard':
        const iceShard = new IceShard(this.scene, startPos, targetPos, 0, isOurSpell);
        this.attachSpellEffects(iceShard);
        this.activeSpells.add(iceShard);
        break;
      case 'lightningBolt':
        const bolt = new LightningBolt(this.scene, startPos, targetPos, 0, isOurSpell);
        this.activeSpells.add(bolt);
        break;
      case 'healSpell':
        const healPos = isFromPlayer1 ? this.playerPosition.clone() : this.enemyPosition.clone();
        new HealSpell(this.scene, healPos);
        break;
      case 'shield':
        const shieldPos = isFromPlayer1 ? this.playerPosition.clone() : this.enemyPosition.clone();
        if (isOurSpell) {
          if (this.playerShield) {
            this.playerShield.dispose();
          }
          this.playerShield = new Shield(this.scene, shieldPos, 1, 2000);
        } else {
          if (this.enemyShield) {
            this.enemyShield.dispose();
          }
          this.enemyShield = new Shield(this.scene, shieldPos, 1, 2000);
        }
        break;
      case 'meteor': {
        const meteorSpell = new MeteorSpell(this.scene, startPos, targetPos, 0, isOurSpell);
        this.attachSpellEffects(meteorSpell);
        this.activeSpells.add(meteorSpell);
        break;
      }
      case 'blizzard': {
        const blizzardSpell = new BlizzardSpell(this.scene, startPos, targetPos, 0, isOurSpell);
        this.attachSpellEffects(blizzardSpell);
        this.activeSpells.add(blizzardSpell);
        break;
      }
      case 'superShield': {
        const ssPos = isFromPlayer1 ? this.playerPosition.clone() : this.enemyPosition.clone();
        if (isOurSpell) {
          if (this.playerSuperShield) this.playerSuperShield.dispose();
          this.playerSuperShield = new SuperShield(this.scene, ssPos, 1, 4000, 0.3);
        } else {
          if (this.enemySuperShield) this.enemySuperShield.dispose();
          this.enemySuperShield = new SuperShield(this.scene, ssPos, 1, 4000, 0.3);
        }
        break;
      }
    }

    // Show gesture indicator for local player
    if (isLocalPlayer) {
      this.uiManager.showGestureIndicator(spell);
    }
  }

  private handleRemoteDamage(target: PlayerRole, amount: number, newHealth: number, shieldAbsorbed: number): void {
    const isTargetUs = target === this.myRole;

    if (shieldAbsorbed > 0) {
      this.audioManager.play('shield_hit');
    }

    if (isTargetUs) {
      // We took damage
      this.gameState.setPlayerHealth(newHealth);
      if (amount > 0) {
        this.playerWizardModel?.triggerDamage();
        this.audioManager.play('damage_taken');
        this.screenShakeIntensity = 0.3;
        if (this.postProcessing) this.postProcessing.triggerDamageFlash(0.4);
      }
    } else {
      // Enemy took damage
      this.gameState.setEnemyHealth(newHealth);
      if (amount > 0) {
        this.enemyWizardModel?.triggerDamage();
      }
    }

    // Play impact sound
    this.audioManager.play('fireball_impact');
  }

  private handleSlowApplied(target: PlayerRole, duration: number, multiplier: number): void {
    const isTargetUs = target === this.myRole;

    if (isTargetUs) {
      this.cooldownManager.applySlow(multiplier, duration);
    }

    // Visual feedback for slow
    this.uiManager.showSlowEffect(target);
  }

  private handleRemotePositionUpdate(player: PlayerRole, x: number, z: number): void {
    // The remote player's position update
    const isPlayer1 = this.myRole === 'player1';

    if ((isPlayer1 && player === 'player2') || (!isPlayer1 && player === 'player1')) {
      // This is our opponent's position
      if (!this.remoteTargetPosition) {
        this.remoteTargetPosition = new THREE.Vector3(x, 1, z);
      } else {
        this.remoteTargetPosition.set(x, 1, z);
      }
    }
  }

  private handleMultiplayerGameOver(winner: PlayerRole): void {
    this.isRunning = false;
    this.showTouchControls(false);
    this.keyboardInput.disable();
    this.uiManager.hideEmotePanel();
    const didWeWin = winner === this.myRole;

    // Trigger victory/defeat visual effects
    if (this.spellEffectsManager) {
      if (didWeWin) {
        this.spellEffectsManager.createVictoryEffect(this.playerPosition);
        this.spellEffectsManager.createDefeatEffect(this.enemyPosition);
      } else {
        this.spellEffectsManager.createDefeatEffect(this.playerPosition);
        this.spellEffectsManager.createVictoryEffect(this.enemyPosition);
      }
    }
    if (this.postProcessing) {
      this.postProcessing.setBloomStrength(1.2);
    }

    // TTS for game over
    this.ttsService.speak(GAME_PHRASES.GAME_OVER);

    this.arenaThemeManager.stopAmbientAudio();

    if (this.myRole === 'spectator') {
      this.audioManager.stopBattleMusic();
      this.uiManager.showMultiplayerGameOver(winner);
    } else {
      if (didWeWin) {
        this.audioManager.playVictoryMusic();
        setTimeout(() => {
          this.ttsService.speak(GAME_PHRASES.VICTORY);
        }, 1500);
      } else {
        this.audioManager.playDefeatMusic();
        setTimeout(() => {
          this.ttsService.speak(GAME_PHRASES.DEFEAT);
        }, 1500);
      }
      this.gameState.endGame(didWeWin ? 'player' : 'enemy');
    }
  }

  private handleCheer(from: string, cheerType: CheerType): void {
    // Play crowd sound with spatial surround effect
    this.audioManager.playCheerSpatial(cheerType);

    // Show cheer indicator
    this.uiManager.showCheerIndicator(from, cheerType);
  }

  // Public method for UI to trigger cheers
  public sendCheer(cheerType: CheerType): void {
    if (this.multiplayerClient && this.isMultiplayer) {
      this.multiplayerClient.cheer(cheerType);
    }
  }

  private async createWizardVisuals(): Promise<void> {
    // Always show procedural models immediately
    const playerWizard = this.createFullWizardModel('archmage', true);
    playerWizard.position.copy(this.playerPosition);
    playerWizard.position.y = 0;
    this.scene.add(playerWizard);
    this.playerHat = playerWizard;

    const enemyWizard = this.createFullWizardModel('archmage', false);
    enemyWizard.position.copy(this.enemyPosition);
    enemyWizard.position.y = 0;
    enemyWizard.rotation.y = Math.PI;
    this.scene.add(enemyWizard);
    this.enemyHat = enemyWizard;

    console.log('Using enhanced procedural wizard models');

    // If GLTF models preferred, load in background and cross-fade
    if (this.useMeshyModels) {
      this.loadAndCrossFadeModel('archmage', playerWizard, this.playerPosition, false);
      this.loadAndCrossFadeModel('archmage', enemyWizard, this.enemyPosition, true);
    }

    // Create name labels for both wizards
    this.createNameLabels();

    // Initialize toon outline post-processing on wizard models
    if (this.postProcessing && this.playerHat && this.enemyHat) {
      this.postProcessing.initOutlines([this.playerHat, this.enemyHat]);
    }
  }

  /** Load a GLTF model in background and cross-fade it over the procedural model */
  private async loadAndCrossFadeModel(
    wizardType: WizardType,
    proceduralGroup: THREE.Group,
    position: THREE.Vector3,
    facePlayer: boolean
  ): Promise<void> {
    try {
      const gltfModel = await modelLoader.loadModel(wizardType);
      gltfModel.scale.set(2, 2, 2);
      gltfModel.position.copy(position);
      gltfModel.position.y = 0;
      if (facePlayer) gltfModel.rotation.y = Math.PI;

      // Apply toon shading to the loaded model
      const { convertToToon } = await import('../utils/ToonMaterialConverter');
      convertToToon(gltfModel, wizardType);

      // Start cross-fade: set GLTF to transparent, add to scene
      this.setGroupOpacity(gltfModel, 0);
      this.scene.add(gltfModel);

      // Animate cross-fade over 500ms
      const fadeStart = performance.now();
      const fadeDuration = 500;
      const fadeStep = () => {
        const elapsed = performance.now() - fadeStart;
        const t = Math.min(elapsed / fadeDuration, 1);
        this.setGroupOpacity(gltfModel, t);
        this.setGroupOpacity(proceduralGroup, 1 - t);

        if (t < 1) {
          requestAnimationFrame(fadeStep);
        } else {
          // Fade complete — remove procedural model
          this.scene.remove(proceduralGroup);

          // Update references
          if (proceduralGroup === this.playerHat) {
            this.playerHat = gltfModel;
          } else if (proceduralGroup === this.enemyHat) {
            this.enemyHat = gltfModel;
          }

          // Update outline objects
          if (this.postProcessing && this.playerHat && this.enemyHat) {
            this.postProcessing.setOutlineObjects([this.playerHat, this.enemyHat]);
          }

          console.log(`GLTF model cross-fade complete for ${wizardType}`);
        }
      };
      requestAnimationFrame(fadeStep);
    } catch (error) {
      console.warn(`Failed to load GLTF model for ${wizardType}, keeping procedural:`, error);
    }
  }

  /** Set opacity on all materials in a group (for cross-fade) */
  private setGroupOpacity(group: THREE.Group, opacity: number): void {
    group.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material) {
        const mat = child.material as THREE.Material;
        mat.transparent = true;
        mat.opacity = opacity;
        mat.needsUpdate = true;
      }
    });
  }

  private createFullWizardModel(wizardType: WizardType, isPlayer: boolean): THREE.Group {
    const wizardModel = new WizardModel(wizardType);
    if (isPlayer) {
      this.playerWizardModel?.dispose();
      this.playerWizardModel = wizardModel;
    } else {
      this.enemyWizardModel?.dispose();
      this.enemyWizardModel = wizardModel;
    }
    return wizardModel.getGroup();
  }

  private createNameLabels(): void {
    // Get wizard names from game state
    const state = this.gameState.getState();
    const playerConfig = WIZARD_CONFIGS[state.player.wizardType];
    const enemyConfig = WIZARD_CONFIGS[state.enemy.wizardType];

    // Create text sprite for player name with (you) indicator
    this.playerNameLabel = this.createTextSprite(`${playerConfig.name} (you)`, '#44FF44');
    this.playerNameLabel.position.copy(this.playerPosition);
    this.playerNameLabel.position.y = 4.5;
    this.scene.add(this.playerNameLabel);

    // Create text sprite for enemy name
    this.enemyNameLabel = this.createTextSprite(enemyConfig.name, '#FF4444');
    this.enemyNameLabel.position.copy(this.enemyPosition);
    this.enemyNameLabel.position.y = 4.5;
    this.scene.add(this.enemyNameLabel);
  }

  private createTextSprite(text: string, color: string = '#FFFFFF'): THREE.Sprite {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d')!;

    // Set canvas size
    canvas.width = 512;
    canvas.height = 128;

    // Configure text style
    context.font = 'Bold 48px Cinzel, serif';
    context.fillStyle = color;
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    // Add text shadow for better visibility
    context.shadowColor = 'rgba(0, 0, 0, 0.8)';
    context.shadowBlur = 8;
    context.shadowOffsetX = 2;
    context.shadowOffsetY = 2;

    // Draw text
    context.fillText(text, 256, 64);

    // Create texture and sprite
    const texture = new THREE.CanvasTexture(canvas);
    const spriteMaterial = new THREE.SpriteMaterial({ map: texture, transparent: true });
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.scale.set(4, 1, 1);

    return sprite;
  }

  private updateNameLabels(wizardType: WizardType, isEnemy: boolean): void {
    const config = WIZARD_CONFIGS[wizardType];
    const name = config.name;

    if (isEnemy && this.enemyNameLabel) {
      // Update enemy name label - dispose old one first
      const oldLabel = this.enemyNameLabel;
      const newLabel = this.createTextSprite(name, '#FF4444');
      newLabel.position.copy(this.enemyPosition);
      newLabel.position.y = 4.5;
      this.scene.remove(oldLabel);
      this.disposeObject3D(oldLabel);
      this.scene.add(newLabel);
      this.enemyNameLabel = newLabel;
    } else if (!isEnemy && this.playerNameLabel) {
      // Update player name label with (you) indicator - dispose old one first
      const oldLabel = this.playerNameLabel;
      const newLabel = this.createTextSprite(`${name} (you)`, '#44FF44');
      newLabel.position.copy(this.playerPosition);
      newLabel.position.y = 4.5;
      this.scene.remove(oldLabel);
      this.disposeObject3D(oldLabel);
      this.scene.add(newLabel);
      this.playerNameLabel = newLabel;
    }
  }

  private async updateWizardAppearance(isPlayer: boolean, wizardType: WizardType): Promise<void> {
    const position = isPlayer ? this.playerPosition : this.enemyPosition;
    const oldModel = isPlayer ? this.playerHat : this.enemyHat;

    // Remove and dispose old model to prevent memory leaks
    if (oldModel) {
      this.scene.remove(oldModel);
      this.disposeObject3D(oldModel);
    }

    // Always show procedural model immediately
    const newModel = this.createFullWizardModel(wizardType, isPlayer);
    newModel.position.set(position.x, 0, position.z);
    if (!isPlayer) {
      newModel.rotation.y = Math.PI;
    }
    this.scene.add(newModel);

    if (isPlayer) {
      this.playerHat = newModel;
    } else {
      this.enemyHat = newModel;
    }

    // Update outline objects
    if (this.postProcessing && this.playerHat && this.enemyHat) {
      this.postProcessing.setOutlineObjects([this.playerHat, this.enemyHat]);
    }

    // If GLTF models preferred, load in background and cross-fade
    if (this.useMeshyModels) {
      this.loadAndCrossFadeModel(wizardType, newModel, position, !isPlayer);
    }

    // Update name label
    this.updateNameLabels(wizardType, !isPlayer);
  }

  private createSpellButtons(): void {
    const controls = document.createElement('div');
    controls.className = 'touch-controls';
    controls.id = 'touch-controls';
    // Hide initially - only show during gameplay
    controls.style.display = 'none';
    controls.innerHTML = `
      <button class="touch-spell-btn" id="spell-fireball" data-spell="fireball" aria-label="Fireball - press Q or 1">
        <span class="spell-key-hint" aria-hidden="true">Q</span>
        <span class="spell-emoji">\u{1F525}</span>
        <span class="spell-label">Fireball</span>
        <div class="cooldown-overlay" id="cd-fireball"></div>
        <div class="cooldown-text" id="cd-text-fireball"></div>
      </button>
      <button class="touch-spell-btn" id="spell-ice" data-spell="iceShard" aria-label="Ice Shard - press W or 2">
        <span class="spell-key-hint" aria-hidden="true">W</span>
        <span class="spell-emoji">\u{2744}\u{FE0F}</span>
        <span class="spell-label">Ice Shard</span>
        <div class="cooldown-overlay" id="cd-iceShard"></div>
        <div class="cooldown-text" id="cd-text-iceShard"></div>
      </button>
      <button class="touch-spell-btn" id="spell-lightning" data-spell="lightningBolt" aria-label="Lightning Bolt - press E or 3">
        <span class="spell-key-hint" aria-hidden="true">E</span>
        <span class="spell-emoji">\u{26A1}</span>
        <span class="spell-label">Lightning</span>
        <div class="cooldown-overlay" id="cd-lightningBolt"></div>
        <div class="cooldown-text" id="cd-text-lightningBolt"></div>
      </button>
      <button class="touch-spell-btn" id="spell-shield" data-spell="shield" aria-label="Shield - press R or 4">
        <span class="spell-key-hint" aria-hidden="true">R</span>
        <span class="spell-emoji">\u{1F6E1}\u{FE0F}</span>
        <span class="spell-label">Shield</span>
        <div class="cooldown-overlay" id="cd-shield"></div>
        <div class="cooldown-text" id="cd-text-shield"></div>
      </button>
      <button class="touch-spell-btn" id="spell-heal" data-spell="healSpell" aria-label="Heal - press F or 5">
        <span class="spell-key-hint" aria-hidden="true">F</span>
        <span class="spell-emoji">\u{1F49A}</span>
        <span class="spell-label">Heal</span>
        <div class="cooldown-overlay" id="cd-healSpell"></div>
        <div class="cooldown-text" id="cd-text-healSpell"></div>
      </button>
      <button class="touch-spell-btn" id="spell-reflect" data-spell="reflect" aria-label="Reflect - press T or 6">
        <span class="spell-key-hint" aria-hidden="true">T</span>
        <span class="spell-emoji">\u{1FA9E}</span>
        <span class="spell-label">Reflect</span>
        <div class="cooldown-overlay" id="cd-reflect"></div>
        <div class="cooldown-text" id="cd-text-reflect"></div>
      </button>
      <button class="touch-spell-btn" id="spell-teleport" data-spell="teleport" aria-label="Teleport - press G or 7">
        <span class="spell-key-hint" aria-hidden="true">G</span>
        <span class="spell-emoji">\u{1F300}</span>
        <span class="spell-label">Teleport</span>
        <div class="cooldown-overlay" id="cd-teleport"></div>
        <div class="cooldown-text" id="cd-text-teleport"></div>
      </button>
    `;
    document.getElementById('app')?.appendChild(controls);

    // Click to cast spells
    const handleSpellCast = (spell: SpellType) => {
      this.onGestureDetected(spell);
    };

    document.getElementById('spell-fireball')?.addEventListener('click', () => handleSpellCast('fireball'));
    document.getElementById('spell-ice')?.addEventListener('click', () => handleSpellCast('iceShard'));
    document.getElementById('spell-lightning')?.addEventListener('click', () => handleSpellCast('lightningBolt'));
    document.getElementById('spell-shield')?.addEventListener('click', () => handleSpellCast('shield'));
    document.getElementById('spell-heal')?.addEventListener('click', () => handleSpellCast('healSpell'));
    document.getElementById('spell-reflect')?.addEventListener('click', () => handleSpellCast('reflect'));
    document.getElementById('spell-teleport')?.addEventListener('click', () => handleSpellCast('teleport'));

    // Prevent double-tap zoom on mobile
    controls.addEventListener('touchend', (e) => {
      e.preventDefault();
    });
  }

  private isMobileDevice(): boolean {
    // Check user agent for mobile devices
    const mobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Check for touch-only device (no mouse)
    const touchOnly = ('ontouchstart' in window) && (navigator.maxTouchPoints > 0) && !window.matchMedia('(pointer: fine)').matches;

    return mobileUserAgent || touchOnly;
  }

  // Lazily initialize camera/gesture detection when needed
  public async initCameraIfNeeded(): Promise<boolean> {
    // Already initialized
    if (this.cameraInitialized) {
      return true;
    }

    // Already initializing - wait for it
    if (this.cameraInitPromise) {
      await this.cameraInitPromise;
      return this.cameraInitialized;
    }

    // Don't init on mobile
    if (this.isMobileDevice()) {
      console.log('Mobile device detected, skipping gesture detection');
      return false;
    }

    // Start initialization
    this.cameraInitPromise = (async () => {
      console.log('Lazily initializing gesture detection...');
      try {
        await this.gestureDetector.init();
        console.log('Gesture detector initialized successfully');
        this.gestureDetector.onGesture(this.onGestureDetected.bind(this));
        this.cameraInitialized = true;
      } catch (error) {
        console.warn('Gesture detection unavailable:', error);
        // Hide webcam container if init failed
        const webcamContainer = document.getElementById('webcam-container');
        if (webcamContainer) {
          webcamContainer.style.display = 'none';
        }
      }
    })();

    await this.cameraInitPromise;
    return this.cameraInitialized;
  }

  public isCameraInitialized(): boolean {
    return this.cameraInitialized;
  }

  public setPracticeMode(enabled: boolean): void {
    this.practiceMode = enabled;
    console.log(`Practice mode ${enabled ? 'enabled' : 'disabled'}`);

    // Stop camera when leaving practice mode to save resources
    if (!enabled && this.cameraInitialized && !this.isRunning) {
      this.stopCamera();
    }
  }

  public stopCamera(): void {
    if (this.gestureDetector.isReady()) {
      console.log('Stopping camera to save resources');
      this.gestureDetector.dispose();
      this.cameraInitialized = false;
      this.cameraInitPromise = null;
    }
  }

  private showTouchControls(visible: boolean): void {
    const controls = document.getElementById('touch-controls');
    if (controls) {
      controls.style.display = visible ? 'flex' : 'none';
    }
  }

  private selectWizard(type: WizardType): void {
    this.gameState.setPlayerWizard(type);

    // In multiplayer, also send to server
    if (this.isMultiplayer && this.multiplayerClient) {
      this.multiplayerClient.selectWizard(type as 'pyromancer' | 'frostmage' | 'battlemage');
    }
  }

  private countdownActive = false;

  private async startGame(): Promise<void> {
    if (this.countdownActive) return;

    if (this.isMultiplayer && this.multiplayerClient) {
      // In multiplayer, tell server we're ready
      this.multiplayerClient.setReady();
    } else {
      // Single player - setup then countdown
      const playerType = this.uiManager.getSelectedWizard();
      this.gameState.setPlayerWizard(playerType);
      this.gameState.startGame();

      const state = this.gameState.getState();
      // Update wizard visuals to match selected types
      await Promise.all([
        this.updateWizardAppearance(true, state.player.wizardType),
        this.updateWizardAppearance(false, state.enemy.wizardType)
      ]);

      this.cpuOpponent.setWizardType(state.enemy.wizardType);
      this.cpuOpponent.reset();
      this.cooldownManager.reset();
      this.cpuCooldownManager.reset();
      this.uiManager.showScreen('playing');

      // Set wizard types for dynamic audio variations
      this.audioManager.setWizardTypes(state.player.wizardType, state.enemy.wizardType);

      // Run countdown before enabling input
      this.countdownActive = true;
      await this.uiManager.showCountdown();
      this.countdownActive = false;

      // Enable input after countdown
      this.isRunning = true;
      this.showTouchControls(true);
      if (loadKeyboardPreference()) {
        this.keyboardInput.enable((spell) => this.onGestureDetected(spell));
      }

      // Start battle music and announce fight
      this.audioManager.playBattleMusic();
      this.ttsService.speak(GAME_PHRASES.FIGHT);
    }
  }

  private async playAgain(): Promise<void> {
    if (this.countdownActive) return;

    this.cleanupSpells();
    if (this.postProcessing) this.postProcessing.resetBloom();

    if (this.isMultiplayer) {
      // In multiplayer, return to lobby
      window.location.reload();
    } else {
      this.gameState.resetGame();
      this.cpuOpponent.reset();
      this.cooldownManager.reset();
      this.cpuCooldownManager.reset();
      this.resetMovement();
      this.uiManager.showScreen('playing');

      // Run countdown before enabling input
      this.countdownActive = true;
      await this.uiManager.showCountdown();
      this.countdownActive = false;

      // Enable input after countdown
      this.isRunning = true;
      this.showTouchControls(true);
      if (loadKeyboardPreference()) {
        this.keyboardInput.enable((spell) => this.onGestureDetected(spell));
      }
      this.audioManager.playBattleMusic();
      this.ttsService.speak(GAME_PHRASES.FIGHT);
    }
  }

  private returnToMenu(): void {
    this.cleanupSpells();
    this.isRunning = false;
    this.showTouchControls(false);
    this.keyboardInput.disable();
    this.audioManager.playMusic(); // Back to menu music
    this.arenaThemeManager.stopAmbientAudio();

    // Stop camera to save resources when returning to menu
    this.stopCamera();

    // Clean up gyroscope input if active
    if (this.gyroscopeInput) {
      this.gyroscopeInput.dispose();
      this.gyroscopeInput = null;
    }

    if (this.isMultiplayer && this.multiplayerClient) {
      this.multiplayerClient.disconnect();
      window.history.pushState({}, '', '/');
    }

    // Clean up economy UI
    if (this.economyUI) {
      this.economyUI.destroy();
      this.economyUI = null;
    }

    this.isMultiplayer = false;
    this.myRole = null;
    this.uiManager.showScreen('menu');
  }

  private showShop(): void {
    this.fetchShopItemsREST();
  }

  private async fetchShopItemsREST(): Promise<void> {
    try {
      const isDev = window.location.host.includes('localhost') || window.location.host.includes('127.0.0.1');
      const apiHost = isDev ? 'http://localhost:8788' : 'https://wizard-duel-api.neenhouse.workers.dev';
      const response = await fetch(`${apiHost}/api/shop/items`);
      if (response.ok) {
        const data = await response.json();
        const items = data.items || data;
        const ownedIds = new Set<string>();
        this.cosmeticManager.getDefinitions().forEach(def => {
          if (this.cosmeticManager.isUnlocked(def.id)) {
            ownedIds.add(def.id);
          }
        });
        const overlay = document.getElementById('ui-overlay');
        if (overlay && this.multiplayerClient) {
          if (!this.economyUI) {
            this.economyUI = new EconomyUI(overlay, this.multiplayerClient, this.dustManager);
          }
          this.economyUI.showShop(items, ownedIds);
        }
      }
    } catch (err) {
      console.warn('Failed to fetch shop items:', err);
    }
  }

  private cleanupSpells(): void {
    this.activeSpells.forEach(spell => spell.dispose());
    this.activeSpells.clear();
    if (this.playerShield) {
      this.playerShield.dispose();
      this.playerShield = null;
    }
    if (this.enemyShield) {
      this.enemyShield.dispose();
      this.enemyShield = null;
    }
    if (this.playerReflect) {
      this.playerReflect.dispose();
      this.playerReflect = null;
    }
    if (this.enemyReflect) {
      this.enemyReflect.dispose();
      this.enemyReflect = null;
    }
    if (this.playerSuperShield) {
      this.playerSuperShield.dispose();
      this.playerSuperShield = null;
    }
    if (this.enemySuperShield) {
      this.enemySuperShield.dispose();
      this.enemySuperShield = null;
    }
    this.playerInvulnerable = false;
    // Clear pending enemy spell and warning
    this.pendingEnemySpell = null;
    this.hideEnemyCastWarning();
    // Reset unique wizard mechanic state
    this.playerDotActive = false;
    this.enemyDotActive = false;
    this.playerHealZoneActive = false;
    this.enemyHealZoneActive = false;
    if (this.playerHealZoneVisual) {
      this.scene.remove(this.playerHealZoneVisual);
      this.playerHealZoneVisual.geometry.dispose();
      (this.playerHealZoneVisual.material as THREE.Material).dispose();
      this.playerHealZoneVisual = null;
    }
    if (this.enemyHealZoneVisual) {
      this.scene.remove(this.enemyHealZoneVisual);
      this.enemyHealZoneVisual.geometry.dispose();
      (this.enemyHealZoneVisual.material as THREE.Material).dispose();
      this.enemyHealZoneVisual = null;
    }
    this.playerRooted = false;
    this.enemyRooted = false;
    this.playerRecentCasts = [];
    this.enemyRecentCasts = [];
    this.playerNextSpellFree = false;
    this.enemyNextSpellFree = false;
    this.playerTimeEchoActive = false;
    this.enemyTimeEchoActive = false;
  }

  private resetMovement(): void {
    this.playerPosition.set(0, 1, 8);
    this.enemyPosition.set(0, 1, -8);
    this.remoteTargetPosition = null;
    this.lastPositionSendTime = 0;
    this.syncWizardVisuals();
  }

  private onGestureDetected(gesture: SpellType, info?: GestureInfo): void {
    // Forward gesture to tutorial if it is active
    if (this.uiManager.isTutorialActive()) {
      this.uiManager.forwardGestureToTutorial(gesture, info);
      this.uiManager.showGestureIndicator(gesture, info);
      return;
    }

    // In practice mode, just show feedback without casting spells
    if (this.practiceMode) {
      this.uiManager.showGestureIndicator(gesture, info);
      return;
    }

    if (!this.isRunning) {
      return;
    }

    // Spectators can't cast spells
    if (this.isMultiplayer && this.myRole === 'spectator') {
      return;
    }

    if (!this.cooldownManager.isReady(gesture)) {
      // Announce cooldown status for accessibility
      const spellDisplayNames: Record<SpellType, string> = {
        fireball: 'Fireball', iceShard: 'Ice Shard', lightningBolt: 'Lightning Bolt',
        shield: 'Shield', healSpell: 'Heal', reflect: 'Reflect', teleport: 'Teleport',
        meteor: 'Meteor', superShield: 'Super Shield', blizzard: 'Blizzard'
      };
      const remaining = Math.ceil(this.cooldownManager.getRemainingTime(gesture) / 1000);
      this.uiManager.announce(`${spellDisplayNames[gesture]} on cooldown, ${remaining} second${remaining !== 1 ? 's' : ''} remaining`);
      return;
    }

    // Prevent casting projectile spells if one is already in flight
    if ((gesture === 'fireball' || gesture === 'iceShard' || gesture === 'lightningBolt' || gesture === 'meteor' || gesture === 'blizzard') && this.playerHasActiveProjectile) {
      console.log('Cannot cast - projectile spell already in flight');
      return;
    }

    const state = this.gameState.getState();
    const config = WIZARD_CONFIGS[state.player.wizardType];

    if (this.isMultiplayer && this.multiplayerClient) {
      // In multiplayer, send spell to server
      this.multiplayerClient.castSpell(gesture);

      // Show gesture feedback with shape info
      this.uiManager.showGestureIndicator(gesture, info);

      // Set local cooldown and active projectile flag
      switch (gesture) {
        case 'fireball':
          this.cooldownManager.setCooldown('fireball', config.fireballCooldown);
          this.playerHasActiveProjectile = true;
          break;
        case 'iceShard':
          this.cooldownManager.setCooldown('iceShard', config.iceShardCooldown);
          this.playerHasActiveProjectile = true;
          break;
        case 'lightningBolt':
          this.cooldownManager.setCooldown('lightningBolt', config.lightningBoltCooldown);
          this.playerHasActiveProjectile = true;
          break;
        case 'shield':
          this.cooldownManager.setCooldown('shield', config.shieldCooldown);
          break;
        case 'healSpell':
          this.cooldownManager.setCooldown('healSpell', config.healCooldown);
          break;
        case 'reflect':
          this.cooldownManager.setCooldown('reflect', config.reflectCooldown);
          break;
        case 'teleport':
          this.cooldownManager.setCooldown('teleport', config.teleportCooldown);
          break;
        case 'meteor':
          this.cooldownManager.setCooldown('meteor', config.meteorCooldown);
          this.playerHasActiveProjectile = true;
          break;
        case 'superShield':
          this.cooldownManager.setCooldown('superShield', config.superShieldCooldown);
          break;
        case 'blizzard':
          this.cooldownManager.setCooldown('blizzard', config.blizzardCooldown);
          this.playerHasActiveProjectile = true;
          break;
      }
    } else {
      // Single player - handle locally
      this.uiManager.showGestureIndicator(gesture, info);

      // Play gesture confirmation sound
      this.audioManager.playGestureConfirm();

      // Record spell for combo detection and AI pattern recognition
      const comboResult = this.gameState.recordSpellCast(true, gesture);
      this.cpuOpponent.recordPlayerSpell(gesture);
      const comboMultiplier = comboResult ? comboResult.damageMultiplier : this.gameState.getComboMultiplier(true);

      // Track for achievements
      this.gameState.recordMatchSpellCast(gesture);
      const isGesture = info !== undefined;
      this.achievementManager.processEvent({
        type: isGesture ? 'gesture_spell_cast' : 'spell_cast',
        data: { spellType: gesture, isGesture },
      });
      if (comboResult) {
        this.gameState.recordMatchCombo();
        this.achievementManager.processEvent({ type: 'combo_triggered', data: {} });
        this.uiManager.showComboPopup(comboResult);
      }

      const playerWizType = this.gameState.getState().player.wizardType;

      // Chronomancer: track spell casts for cooldown reset
      if (playerWizType === 'chronomancer') {
        const now = Date.now();
        this.playerRecentCasts.push(now);
        this.playerRecentCasts = this.playerRecentCasts.filter(t => now - t < 8000);
        if (this.playerRecentCasts.length >= 3 && !this.playerNextSpellFree) {
          this.playerNextSpellFree = true;
          this.playerRecentCasts = [];
        }
      }

      // Chronomancer: if next spell is free, skip cooldown
      const skipCooldown = playerWizType === 'chronomancer' && this.playerNextSpellFree;
      if (skipCooldown) {
        this.playerNextSpellFree = false;
      }

      switch (gesture) {
        case 'fireball':
          this.castFireball(true, Math.round(config.fireballDamage * comboMultiplier));
          if (!skipCooldown) this.cooldownManager.setCooldown('fireball', config.fireballCooldown);
          this.playerHasActiveProjectile = true;
          this.audioManager.playSpellSound('fireball_cast', playerWizType);
          this.ttsService.speak(GAME_PHRASES.FIREBALL);
          break;

        case 'iceShard':
          this.castIceShard(true, Math.round(config.iceShardDamage * comboMultiplier));
          if (!skipCooldown) this.cooldownManager.setCooldown('iceShard', config.iceShardCooldown);
          this.playerHasActiveProjectile = true;
          this.audioManager.playSpellSound('iceShard_cast', playerWizType);
          this.ttsService.speak(GAME_PHRASES.ICE_SHARD);
          break;

        case 'lightningBolt':
          this.castLightningBolt(true, Math.round(config.lightningBoltDamage * comboMultiplier));
          if (!skipCooldown) this.cooldownManager.setCooldown('lightningBolt', config.lightningBoltCooldown);
          this.playerHasActiveProjectile = true;
          this.audioManager.playSpellSound('lightningBolt_cast', playerWizType);
          this.ttsService.speak(GAME_PHRASES.LIGHTNING_BOLT);
          break;

        case 'shield':
          this.castShield(true, config.shieldStrength, 2000);
          if (!skipCooldown) this.cooldownManager.setCooldown('shield', config.shieldCooldown);
          this.audioManager.playSpellSound('shield_cast', playerWizType);
          this.ttsService.speak(GAME_PHRASES.SHIELD);
          break;

        case 'healSpell':
          this.castHeal(true, config.healAmount);
          if (!skipCooldown) this.cooldownManager.setCooldown('healSpell', config.healCooldown);
          this.audioManager.playSpellSound('heal_cast', playerWizType);
          // Druid: create healing zone on heal cast
          if (playerWizType === 'druid') {
            this.createHealingZone(true);
          }
          break;

        case 'reflect':
          this.castReflect(true, config.reflectDuration, config.reflectDamageReturn);
          this.cooldownManager.setCooldown('reflect', config.reflectCooldown);
          this.audioManager.playSpellSound('shield_cast', playerWizType);
          break;

        case 'teleport':
          this.castTeleport(true);
          this.cooldownManager.setCooldown('teleport', config.teleportCooldown);
          this.audioManager.playSpellSound('shield_cast', playerWizType);
          // Chronomancer: teleport leaves a time echo that absorbs one hit
          if (playerWizType === 'chronomancer') {
            this.playerTimeEchoActive = true;
          }
          break;

        case 'meteor':
          this.castMeteor(true, config.meteorDamage, config.meteorChargeTime);
          if (!skipCooldown) this.cooldownManager.setCooldown('meteor', config.meteorCooldown);
          this.playerHasActiveProjectile = true;
          this.audioManager.playSpellSound('fireball_cast', playerWizType);
          break;

        case 'superShield':
          this.castSuperShield(true, config.superShieldStrength, config.superShieldDuration, config.superShieldReflect);
          if (!skipCooldown) this.cooldownManager.setCooldown('superShield', config.superShieldCooldown);
          this.audioManager.playSpellSound('shield_cast', playerWizType);
          break;

        case 'blizzard':
          this.castBlizzard(true, config.blizzardDamage);
          if (!skipCooldown) this.cooldownManager.setCooldown('blizzard', config.blizzardCooldown);
          this.audioManager.playSpellSound('iceShard_cast', playerWizType);
          break;
      }
    }
  }

  private castFireball(isPlayer: boolean, damage: number): void {
    const startPos = isPlayer ? this.playerPosition.clone() : this.enemyPosition.clone();
    const targetPos = isPlayer ? this.enemyPosition.clone() : this.playerPosition.clone();

    startPos.y = 1.5;
    targetPos.y = 1.5;

    const fireball = new Fireball(this.scene, startPos, targetPos, damage, isPlayer);
    // Set live target so hit detection tracks wizard movement
    fireball.setLiveTarget(isPlayer ? this.enemyPosition : this.playerPosition);
    this.attachSpellEffects(fireball);
    this.activeSpells.add(fireball);

    // Trigger casting animation
    const model = isPlayer ? this.playerWizardModel : this.enemyWizardModel;
    model?.triggerCast('fireball');

    if (isPlayer) {
      this.cpuOpponent.notifyIncomingProjectile();
    }
  }

  private castIceShard(isPlayer: boolean, damage: number): void {
    const startPos = isPlayer ? this.playerPosition.clone() : this.enemyPosition.clone();
    const targetPos = isPlayer ? this.enemyPosition.clone() : this.playerPosition.clone();

    startPos.y = 1.5;
    targetPos.y = 1.5;

    const iceShard = new IceShard(this.scene, startPos, targetPos, damage, isPlayer);
    iceShard.setLiveTarget(isPlayer ? this.enemyPosition : this.playerPosition);
    this.attachSpellEffects(iceShard);
    this.activeSpells.add(iceShard);

    // Trigger casting animation
    const model = isPlayer ? this.playerWizardModel : this.enemyWizardModel;
    model?.triggerCast('iceShard');

    if (isPlayer) {
      this.cpuOpponent.notifyIncomingProjectile();
    }
  }

  private castLightningBolt(isPlayer: boolean, damage: number): void {
    const startPos = isPlayer ? this.playerPosition.clone() : this.enemyPosition.clone();
    const targetPos = isPlayer ? this.enemyPosition.clone() : this.playerPosition.clone();

    startPos.y = 1.5;
    targetPos.y = 1.5;

    const bolt = new LightningBolt(this.scene, startPos, targetPos, damage, isPlayer);
    bolt.setLiveTarget(isPlayer ? this.enemyPosition : this.playerPosition);
    this.activeSpells.add(bolt);

    // Trigger casting animation
    const model = isPlayer ? this.playerWizardModel : this.enemyWizardModel;
    model?.triggerCast('lightningBolt');

    if (isPlayer) {
      this.cpuOpponent.notifyIncomingProjectile();
    }
  }

  private castHeal(isPlayer: boolean, healAmount: number): void {
    const position = isPlayer ? this.playerPosition.clone() : this.enemyPosition.clone();

    // Create heal visual effect
    new HealSpell(this.scene, position);

    // Apply healing
    if (isPlayer) {
      const state = this.gameState.getState();
      const maxHealth = WIZARD_CONFIGS[state.player.wizardType].health;
      const newHealth = Math.min(maxHealth, state.player.health + healAmount);
      this.gameState.setPlayerHealth(newHealth);
    } else {
      const state = this.gameState.getState();
      const maxHealth = WIZARD_CONFIGS[state.enemy.wizardType].health;
      const newHealth = Math.min(maxHealth, state.enemy.health + healAmount);
      this.gameState.setEnemyHealth(newHealth);
    }

    // Trigger casting animation
    const model = isPlayer ? this.playerWizardModel : this.enemyWizardModel;
    model?.triggerCast('healSpell');
  }

  private castShield(isPlayer: boolean, strength: number, duration: number): void {
    const position = isPlayer ? this.playerPosition.clone() : this.enemyPosition.clone();

    // Trigger casting animation
    const model = isPlayer ? this.playerWizardModel : this.enemyWizardModel;
    model?.triggerCast('shield');

    // Create shield ripple on the arena floor
    if (this.environmentalEffects) {
      this.environmentalEffects.createShieldRipple(position);
    }

    if (isPlayer) {
      if (this.playerShield) {
        this.playerShield.dispose();
      }
      this.playerShield = new Shield(this.scene, position, strength, duration);
    } else {
      if (this.enemyShield) {
        this.enemyShield.dispose();
      }
      this.enemyShield = new Shield(this.scene, position, strength, duration);
    }
  }

  private castReflect(isPlayer: boolean, duration: number, damageReturn: number): void {
    const position = isPlayer ? this.playerPosition.clone() : this.enemyPosition.clone();

    const model = isPlayer ? this.playerWizardModel : this.enemyWizardModel;
    model?.triggerCast('reflect');

    if (isPlayer) {
      if (this.playerReflect) this.playerReflect.dispose();
      this.playerReflect = new ReflectBarrier(this.scene, position, duration, damageReturn);
    } else {
      if (this.enemyReflect) this.enemyReflect.dispose();
      this.enemyReflect = new ReflectBarrier(this.scene, position, duration, damageReturn);
    }
  }

  private castTeleport(isPlayer: boolean): void {
    const currentPos = isPlayer ? this.playerPosition.clone() : this.enemyPosition.clone();
    const newPos = currentPos.clone();
    newPos.x = -currentPos.x;
    if (Math.abs(newPos.x - currentPos.x) < 0.5) {
      newPos.x = currentPos.x > 0 ? -3 : 3;
    }
    const ARENA_BOUND = 6;
    newPos.x = Math.max(-ARENA_BOUND, Math.min(ARENA_BOUND, newPos.x));

    new TeleportSpell(this.scene, currentPos, newPos);

    if (isPlayer) {
      this.playerPosition.x = newPos.x;
      const config = WIZARD_CONFIGS[this.gameState.getState().player.wizardType];
      this.playerInvulnerable = true;
      this.playerInvulnerableEndTime = Date.now() + config.teleportInvulnerability;
    } else {
      this.enemyPosition.x = newPos.x;
    }

    // Update wizard visual positions
    if (this.playerHat && isPlayer) {
      this.playerHat.position.set(this.playerPosition.x, 0, this.playerPosition.z);
    }
    if (this.enemyHat && !isPlayer) {
      this.enemyHat.position.set(this.enemyPosition.x, 0, this.enemyPosition.z);
    }

    const model = isPlayer ? this.playerWizardModel : this.enemyWizardModel;
    model?.triggerCast('teleport');
  }

  // --- Ultimate Spell Casting Methods ---

  private castMeteor(isPlayer: boolean, damage: number, chargeTime: number = 2): void {
    const startPos = isPlayer ? this.playerPosition.clone() : this.enemyPosition.clone();
    const targetPos = isPlayer ? this.enemyPosition.clone() : this.playerPosition.clone();
    startPos.y = 1.5;
    targetPos.y = 1.5;

    const meteor = new MeteorSpell(this.scene, startPos, targetPos, damage, isPlayer, chargeTime);
    meteor.setLiveTarget(isPlayer ? this.enemyPosition : this.playerPosition);
    this.attachSpellEffects(meteor);
    this.activeSpells.add(meteor);

    // Screen shake on cast for epic feel
    this.screenShakeIntensity = Math.max(this.screenShakeIntensity, 0.3);

    const castModel = isPlayer ? this.playerWizardModel : this.enemyWizardModel;
    castModel?.triggerCast('meteor');

    if (isPlayer) {
      this.cpuOpponent.notifyIncomingProjectile();
    }
  }

  private castSuperShield(isPlayer: boolean, strength: number, duration: number, reflectFraction: number): void {
    const position = isPlayer ? this.playerPosition.clone() : this.enemyPosition.clone();

    const castModel = isPlayer ? this.playerWizardModel : this.enemyWizardModel;
    castModel?.triggerCast('superShield');

    if (this.environmentalEffects) {
      this.environmentalEffects.createShieldRipple(position);
    }

    if (isPlayer) {
      // Super Shield replaces normal shield
      if (this.playerShield) {
        this.playerShield.dispose();
        this.playerShield = null;
      }
      if (this.playerSuperShield) {
        this.playerSuperShield.dispose();
      }
      this.playerSuperShield = new SuperShield(this.scene, position, strength, duration, reflectFraction);
    } else {
      if (this.enemyShield) {
        this.enemyShield.dispose();
        this.enemyShield = null;
      }
      if (this.enemySuperShield) {
        this.enemySuperShield.dispose();
      }
      this.enemySuperShield = new SuperShield(this.scene, position, strength, duration, reflectFraction);
    }
  }

  private castBlizzard(isPlayer: boolean, damage: number): void {
    const startPos = isPlayer ? this.playerPosition.clone() : this.enemyPosition.clone();
    const targetPos = isPlayer ? this.enemyPosition.clone() : this.playerPosition.clone();
    startPos.y = 1.5;
    targetPos.y = 1.5;

    const blizzard = new BlizzardSpell(this.scene, startPos, targetPos, damage, isPlayer);
    blizzard.setLiveTarget(isPlayer ? this.enemyPosition : this.playerPosition);
    this.attachSpellEffects(blizzard);
    this.activeSpells.add(blizzard);

    const castModel = isPlayer ? this.playerWizardModel : this.enemyWizardModel;
    castModel?.triggerCast('blizzard');

    if (isPlayer) {
      this.cpuOpponent.notifyIncomingProjectile();
    }
  }

  private updateMovement(delta: number): void {
    // Spectators and multiplayer observers don't control movement
    const canMove = (!this.isMultiplayer || (this.myRole === 'player1' || this.myRole === 'player2')) && !this.playerRooted;

    // --- Multiplayer: interpolate remote player's enemy position ---
    if (this.remoteTargetPosition) {
      // Multiplayer: interpolate remote player's enemy position
      const isPlayer1 = this.myRole === 'player1';
      const remotePos = isPlayer1 ? this.enemyPosition : this.playerPosition;
      const dx = this.remoteTargetPosition.x - remotePos.x;
      const dz = this.remoteTargetPosition.z - remotePos.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist > 0.05) {
        const lerp = Math.min(1, this.remoteInterpolationSpeed * delta);
        remotePos.x += dx * lerp;
        remotePos.z += dz * lerp;
      }
    }

    // --- Sync visual positions to data positions ---
    this.syncWizardVisuals();

    // --- Multiplayer: send our position to server ---
    if (this.isMultiplayer && this.multiplayerClient && canMove) {
      const now = performance.now();
      if (now - this.lastPositionSendTime >= this.POSITION_SEND_INTERVAL) {
        this.lastPositionSendTime = now;
        this.multiplayerClient.sendPosition(this.playerPosition.x, this.playerPosition.z);
      }
    }
  }

  /** Sync wizard 3D group positions, name labels, and shield positions to current data */
  private syncWizardVisuals(): void {
    // Player wizard
    if (this.playerHat) {
      this.playerHat.position.x = this.playerPosition.x;
      this.playerHat.position.z = this.playerPosition.z;
    }
    if (this.playerNameLabel) {
      this.playerNameLabel.position.x = this.playerPosition.x;
      this.playerNameLabel.position.z = this.playerPosition.z;
    }
    if (this.playerShield) {
      this.playerShield.setPosition(this.playerPosition);
    }

    // Enemy wizard
    if (this.enemyHat) {
      this.enemyHat.position.x = this.enemyPosition.x;
      this.enemyHat.position.z = this.enemyPosition.z;
    }
    if (this.enemyNameLabel) {
      this.enemyNameLabel.position.x = this.enemyPosition.x;
      this.enemyNameLabel.position.z = this.enemyPosition.z;
    }
    if (this.enemyShield) {
      this.enemyShield.setPosition(this.enemyPosition);
    }

    // Keep enemy cast warning position in sync
    if (this.enemyCastWarning) {
      this.enemyCastWarning.position.x = this.enemyPosition.x;
      this.enemyCastWarning.position.z = this.enemyPosition.z;
    }
  }

  private updateCPU(delta: number): void {
    // Skip CPU updates in multiplayer
    if (this.isMultiplayer) return;

    // Update combo meter decay
    this.gameState.updateComboDecay(delta);

    // Notify CPU of player shield state for tactical decisions
    this.cpuOpponent.notifyPlayerShieldState(
      this.playerShield !== null && this.playerShield.isShieldActive()
    );
    // Process pending enemy spell after delay
    if (this.pendingEnemySpell) {
      const elapsed = Date.now() - this.pendingEnemySpellTime;

      // Update warning visual animation
      this.updateEnemyCastWarning(elapsed / Game.ENEMY_CAST_DELAY);

      if (elapsed >= Game.ENEMY_CAST_DELAY) {
        // Execute the queued spell
        const state = this.gameState.getState();
        const config = WIZARD_CONFIGS[state.enemy.wizardType];

        const enemyWizType = state.enemy.wizardType;

        // Chronomancer enemy: track casts for cooldown reset
        if (enemyWizType === 'chronomancer') {
          const now = Date.now();
          this.enemyRecentCasts.push(now);
          this.enemyRecentCasts = this.enemyRecentCasts.filter(t => now - t < 8000);
          if (this.enemyRecentCasts.length >= 3 && !this.enemyNextSpellFree) {
            this.enemyNextSpellFree = true;
            this.enemyRecentCasts = [];
          }
        }

        // Track enemy combo for damage multiplier
        const enemyCombo = this.gameState.recordSpellCast(false, this.pendingEnemySpell);
        const enemyComboMult = enemyCombo ? enemyCombo.damageMultiplier : this.gameState.getComboMultiplier(false);
        switch (this.pendingEnemySpell) {
          case 'fireball':
            this.castFireball(false, Math.round(config.fireballDamage * enemyComboMult));
            this.audioManager.playSpellSound('fireball_cast', enemyWizType);
            break;
          case 'iceShard':
            this.castIceShard(false, Math.round(config.iceShardDamage * enemyComboMult));
            this.audioManager.playSpellSound('iceShard_cast', enemyWizType);
            break;
          case 'lightningBolt':
            this.castLightningBolt(false, Math.round(config.lightningBoltDamage * enemyComboMult));
            this.audioManager.playSpellSound('lightningBolt_cast', enemyWizType);
            break;
          case 'meteor':
            this.castMeteor(false, config.meteorDamage, config.meteorChargeTime);
            this.audioManager.playSpellSound('fireball_cast', enemyWizType);
            break;
          case 'blizzard':
            this.castBlizzard(false, config.blizzardDamage);
            this.audioManager.playSpellSound('iceShard_cast', enemyWizType);
            break;
        }

        // Clear pending spell and remove warning
        this.pendingEnemySpell = null;
        this.hideEnemyCastWarning();
      }
      return; // Don't process new actions while spell is pending
    }

    const action = this.cpuOpponent.update(delta);

    if (action && action.type === 'cast') {
      const state = this.gameState.getState();
      const config = WIZARD_CONFIGS[state.enemy.wizardType];

      // Offensive spells get a 2-second telegraph delay
      if (action.spell === 'fireball' || action.spell === 'iceShard' || action.spell === 'lightningBolt') {
        this.pendingEnemySpell = action.spell;
        this.pendingEnemySpellTime = Date.now();
        this.showEnemyCastWarning(action.spell);
      } else if (action.spell === 'meteor' || action.spell === 'blizzard') {
        // Ultimate offensive spells also get telegraph delay
        this.pendingEnemySpell = action.spell;
        this.pendingEnemySpellTime = Date.now();
        this.showEnemyCastWarning(action.spell);
      } else if (action.spell === 'healSpell') {
        // Heal casts immediately (self-cast)
        this.castHeal(false, config.healAmount);
        this.audioManager.playSpellSound('heal_cast', state.enemy.wizardType);
        // Druid: create healing zone on heal cast
        if (state.enemy.wizardType === 'druid') {
          this.createHealingZone(false);
        }
      } else if (action.spell === 'superShield') {
        // Super Shield casts immediately (defensive ultimate)
        this.castSuperShield(false, config.superShieldStrength, config.superShieldDuration, config.superShieldReflect);
        this.audioManager.playSpellSound('shield_cast', state.enemy.wizardType);
      } else {
        // Shield casts immediately (defensive)
        switch (action.spell) {
          case 'shield':
            this.castShield(false, config.shieldStrength, 2000);
            this.audioManager.playSpellSound('shield_cast', state.enemy.wizardType);
            break;
        }
      }
    }
  }

  private showEnemyCastWarning(spell: SpellType): void {
    // Remove existing warning if any
    this.hideEnemyCastWarning();

    const group = new THREE.Group();

    // Determine color based on spell type
    const color = (spell === 'fireball' || spell === 'meteor') ? 0xFF4400 : spell === 'lightningBolt' ? 0xFFD700 : 0x44AAFF;
    const emissiveColor = (spell === 'fireball' || spell === 'meteor') ? 0xFF2200 : spell === 'lightningBolt' ? 0xFFAA00 : 0x2288FF;

    // Large pulsing warning ring (200% larger than before - very obvious)
    const ringGeometry = new THREE.RingGeometry(1.5, 2.0, 32);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.1;
    ring.name = 'warningRing';
    group.add(ring);

    // Second larger outer ring for emphasis
    const outerRingGeometry = new THREE.RingGeometry(2.2, 2.5, 32);
    const outerRingMaterial = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.5,
      side: THREE.DoubleSide
    });
    const outerRing = new THREE.Mesh(outerRingGeometry, outerRingMaterial);
    outerRing.rotation.x = -Math.PI / 2;
    outerRing.position.y = 0.1;
    outerRing.name = 'outerWarningRing';
    group.add(outerRing);

    // Large glowing orb above enemy (the charging spell indicator)
    const orbGeometry = new THREE.SphereGeometry(0.8, 32, 32);
    const orbMaterial = new THREE.MeshStandardMaterial({
      color: color,
      emissive: emissiveColor,
      emissiveIntensity: 1.5,
      transparent: true,
      opacity: 0.9
    });
    const orb = new THREE.Mesh(orbGeometry, orbMaterial);
    orb.position.y = 4.5;
    orb.name = 'chargingOrb';
    group.add(orb);

    // Spell icon indicator (text sprite would be ideal, but use geometry)
    const iconGeometry = spell === 'fireball'
      ? new THREE.ConeGeometry(0.4, 0.8, 8) // Flame shape
      : new THREE.OctahedronGeometry(0.5);   // Ice crystal shape
    const iconMaterial = new THREE.MeshBasicMaterial({
      color: 0xFFFFFF,
      transparent: true,
      opacity: 0.9
    });
    const icon = new THREE.Mesh(iconGeometry, iconMaterial);
    icon.position.y = 4.5;
    icon.name = 'spellIcon';
    group.add(icon);

    // Add bright point light for dramatic effect
    const light = new THREE.PointLight(color, 3, 10);
    light.position.y = 4.5;
    group.add(light);

    // Add a second light at ground level
    const groundLight = new THREE.PointLight(color, 2, 8);
    groundLight.position.y = 0.5;
    group.add(groundLight);

    // Position at enemy location
    group.position.copy(this.enemyPosition);
    group.position.y = 0;

    this.scene.add(group);
    this.enemyCastWarning = group;
  }

  private updateEnemyCastWarning(progress: number): void {
    if (!this.enemyCastWarning) return;

    const time = Date.now() * 0.005;

    // Pulse the rings
    const ring = this.enemyCastWarning.getObjectByName('warningRing') as THREE.Mesh;
    const outerRing = this.enemyCastWarning.getObjectByName('outerWarningRing') as THREE.Mesh;
    const orb = this.enemyCastWarning.getObjectByName('chargingOrb') as THREE.Mesh;
    const icon = this.enemyCastWarning.getObjectByName('spellIcon') as THREE.Mesh;

    if (ring) {
      const pulseScale = 1 + Math.sin(time * 4) * 0.15;
      ring.scale.set(pulseScale, pulseScale, 1);
      (ring.material as THREE.MeshBasicMaterial).opacity = 0.5 + Math.sin(time * 6) * 0.3;
    }

    if (outerRing) {
      const pulseScale = 1 + Math.sin(time * 3 + 1) * 0.2;
      outerRing.scale.set(pulseScale, pulseScale, 1);
      outerRing.rotation.z += 0.02;
    }

    if (orb) {
      // Grow the orb as the cast progresses
      const scale = 0.5 + progress * 1.0; // Grows from 0.5 to 1.5
      orb.scale.set(scale, scale, scale);
      (orb.material as THREE.MeshStandardMaterial).emissiveIntensity = 1.0 + progress * 2.0;
    }

    if (icon) {
      icon.rotation.y += 0.05;
      icon.rotation.z = Math.sin(time * 2) * 0.3;
    }
  }

  private hideEnemyCastWarning(): void {
    if (this.enemyCastWarning) {
      this.scene.remove(this.enemyCastWarning);
      this.disposeObject3D(this.enemyCastWarning);
      this.enemyCastWarning = null;
    }
  }

  private updateSpells(delta: number): void {
    // In multiplayer, damage is handled by server, so spells are just visual
    if (this.isMultiplayer) {
      const spellsToRemove: Spell[] = [];

      this.activeSpells.forEach(spell => {
        const isActive = spell.update(delta);
        if (!isActive) {
          spell.triggerImpact();
          spellsToRemove.push(spell);
        }
      });

      // O(1) removal with Set
      spellsToRemove.forEach(spell => this.activeSpells.delete(spell));
      return;
    }

    // Single player spell handling

    // --- Spell collision detection (before individual resolution) ---
    if (this.spellCollisionSystem) {
      const collisions = this.spellCollisionSystem.checkCollisions(this.activeSpells);
      for (const collision of collisions) {
        this.spellCollisionSystem.createCollisionEffect(collision);
        const { playerDamage, enemyDamage } = this.spellCollisionSystem.getCollisionDamage(collision);
        if (playerDamage > 0) {
          this.gameState.damagePlayer(playerDamage);
          this.playerWizardModel?.triggerDamage();
          this.screenShakeIntensity = 0.2;
        }
        if (enemyDamage > 0) {
          this.gameState.damageEnemy(enemyDamage);
          this.enemyWizardModel?.triggerDamage();
        }
        collision.spell1.dispose();
        collision.spell2.dispose();
        this.activeSpells.delete(collision.spell1);
        this.activeSpells.delete(collision.spell2);
        if (collision.spell1.isFromPlayer() || collision.spell2.isFromPlayer()) {
          this.playerHasActiveProjectile = false;
        }
      }
    }

    // --- Update reflect barriers ---
    if (this.playerReflect && !this.playerReflect.update(delta)) {
      this.playerReflect = null;
    }
    if (this.enemyReflect && !this.enemyReflect.update(delta)) {
      this.enemyReflect = null;
    }

    // --- Update teleport invulnerability ---
    if (this.playerInvulnerable && Date.now() > this.playerInvulnerableEndTime) {
      this.playerInvulnerable = false;
    }

    const spellsToRemove: Spell[] = [];

    this.activeSpells.forEach(spell => {
      const isActive = spell.update(delta);

      if (!isActive) {
        const damage = spell.getDamage();
        const isPlayerSpell = spell.isFromPlayer();
        const didHit = spell.didHit();
        const spellType = spell.getType();

        // Clear active projectile flag when player's projectile spell completes
        if (isPlayerSpell && (spellType === 'fireball' || spellType === 'iceShard' || spellType === 'lightningBolt' || spellType === 'meteor' || spellType === 'blizzard')) {
          this.playerHasActiveProjectile = false;
        }

        if (didHit) {
          const hitState = this.gameState.getState();

          // Player spell hits enemy
          if (isPlayerSpell) {
            // Check enemy reflect barrier (lightning passes through)
            if (this.enemyReflect && this.enemyReflect.isBarrierActive() && spellType !== 'lightningBolt') {
              const reflectedDamage = this.enemyReflect.reflectProjectile(damage);
              this.gameState.damagePlayer(reflectedDamage);
              this.playerWizardModel?.triggerDamage();
              this.screenShakeIntensity = 0.3;
              if (this.postProcessing) this.postProcessing.triggerDamageFlash(0.4);
              this.audioManager.play('damage_taken');
            } else if (this.enemyTimeEchoActive) {
              // Chronomancer enemy time echo absorbs one hit
              this.enemyTimeEchoActive = false;
            } else if (this.enemySuperShield && this.enemySuperShield.isShieldActive()) {
              const result = this.enemySuperShield.takeDamage(damage);
              this.audioManager.playSpellSound('shield_hit', hitState.player.wizardType);
              if (result.reflected > 0) {
                this.gameState.damagePlayer(result.reflected);
                this.playerWizardModel?.triggerDamage();
                this.screenShakeIntensity = 0.2;
              }
              if (result.remaining > 0) {
                this.gameState.damageEnemy(result.remaining);
                this.enemyWizardModel?.triggerDamage();
              }
            } else if (this.enemyShield && this.enemyShield.isShieldActive()) {
              const overflow = this.enemyShield.takeDamage(damage);
              this.audioManager.playSpellSound('shield_hit', hitState.player.wizardType);
              if (overflow > 0) {
                this.gameState.damageEnemy(overflow);
                this.enemyWizardModel?.triggerDamage();
              }
            } else {
              this.gameState.damageEnemy(damage);
              this.gameState.recordMatchDamageDealt(damage);
              this.enemyWizardModel?.triggerDamage();
              this.audioManager.registerComboHit();
              // Necromancer: life-steal (heal caster for 18% of damage dealt)
              if (hitState.player.wizardType === 'necromancer') {
                const lifeSteal = Math.round(damage * 0.18);
                if (lifeSteal > 0) {
                  const maxHp = WIZARD_CONFIGS[hitState.player.wizardType].health;
                  const newHp = Math.min(maxHp, hitState.player.health + lifeSteal);
                  this.gameState.setPlayerHealth(newHp);
                }
              }
              if (spellType === 'iceShard') {
                if (hitState.player.wizardType === 'necromancer') {
                  // Necromancer: DoT instead of slow (3 HP/sec for 3 seconds)
                  this.enemyDotActive = true;
                  this.enemyDotEndTime = Date.now() + 3000;
                  this.enemyDotDps = 3;
                } else if (hitState.player.wizardType === 'druid') {
                  // Druid: root instead of slow (1.5 seconds)
                  this.enemyRooted = true;
                  this.enemyRootEndTime = Date.now() + 1500;
                } else {
                  this.cpuCooldownManager.applySlow(1.5, 3000);
                }
              }
              // Blizzard: apply slow + DOT on hit
              if (spellType === 'blizzard') {
                const blizzardConfig = WIZARD_CONFIGS[hitState.player.wizardType];
                this.cpuCooldownManager.applySlow(1.5, blizzardConfig.blizzardDotDuration);
                this.enemyDotActive = true;
                this.enemyDotEndTime = Date.now() + blizzardConfig.blizzardDotDuration;
                this.enemyDotDps = blizzardConfig.blizzardDotDamage;
              }
            }
            this.cpuOpponent.recordPlayerHit();
          }
          // Enemy spell hits player
          else {
            // Check player reflect barrier (lightning passes through)
            if (this.playerReflect && this.playerReflect.isBarrierActive() && spellType !== 'lightningBolt') {
              const reflectedDamage = this.playerReflect.reflectProjectile(damage);
              this.gameState.damageEnemy(reflectedDamage);
              this.enemyWizardModel?.triggerDamage();
            } else if (this.playerInvulnerable) {
              // Teleport invulnerability - no damage
            } else if (this.playerTimeEchoActive) {
              // Chronomancer time echo absorbs one hit
              this.playerTimeEchoActive = false;
            } else if (this.playerSuperShield && this.playerSuperShield.isShieldActive()) {
              const result = this.playerSuperShield.takeDamage(damage);
              this.audioManager.playSpellSound('shield_hit', hitState.enemy.wizardType);
              this.gameState.recordMatchShieldBlock();
              this.achievementManager.processEvent({ type: 'shield_blocked', data: {} });
              if (result.reflected > 0) {
                this.gameState.damageEnemy(result.reflected);
                this.enemyWizardModel?.triggerDamage();
              }
              if (result.remaining > 0) {
                this.gameState.damagePlayer(result.remaining);
                this.playerWizardModel?.triggerDamage();
                this.screenShakeIntensity = 0.2;
              }
            } else if (this.playerShield && this.playerShield.isShieldActive()) {
              const overflow = this.playerShield.takeDamage(damage);
              this.audioManager.playSpellSound('shield_hit', hitState.enemy.wizardType);
              this.gameState.recordMatchShieldBlock();
              this.achievementManager.processEvent({ type: 'shield_blocked', data: {} });
              if (overflow > 0) {
                this.gameState.damagePlayer(overflow);
                this.playerWizardModel?.triggerDamage();
                this.screenShakeIntensity = 0.2;
              }
            } else {
              this.gameState.damagePlayer(damage);
              this.gameState.recordMatchDamageTaken(damage);
              this.playerWizardModel?.triggerDamage();
              this.audioManager.play('damage_taken');
              this.screenShakeIntensity = 0.3;
              if (this.postProcessing) this.postProcessing.triggerDamageFlash(0.4);
              // Necromancer enemy: life-steal
              if (hitState.enemy.wizardType === 'necromancer') {
                const lifeSteal = Math.round(damage * 0.18);
                if (lifeSteal > 0) {
                  const maxHp = WIZARD_CONFIGS[hitState.enemy.wizardType].health;
                  const newHp = Math.min(maxHp, hitState.enemy.health + lifeSteal);
                  this.gameState.setEnemyHealth(newHp);
                }
              }
              if (spellType === 'iceShard') {
                if (hitState.enemy.wizardType === 'necromancer') {
                  this.playerDotActive = true;
                  this.playerDotEndTime = Date.now() + 3000;
                  this.playerDotDps = 3;
                } else if (hitState.enemy.wizardType === 'druid') {
                  this.playerRooted = true;
                  this.playerRootEndTime = Date.now() + 1500;
                } else {
                  this.cooldownManager.applySlow(1.5, 3000);
                }
              }
              // Blizzard: apply slow + DOT on hit
              if (spellType === 'blizzard') {
                const blizzardConfig = WIZARD_CONFIGS[hitState.enemy.wizardType];
                this.cooldownManager.applySlow(1.5, blizzardConfig.blizzardDotDuration);
                this.playerDotActive = true;
                this.playerDotEndTime = Date.now() + blizzardConfig.blizzardDotDuration;
                this.playerDotDps = blizzardConfig.blizzardDotDamage;
              }
            }
            this.cpuOpponent.recordCPUHit();
          }

          // Play impact sound with wizard-specific variant
          const impactWizType = isPlayerSpell ? hitState.player.wizardType : hitState.enemy.wizardType;
          if (spellType === 'fireball' || spellType === 'meteor') {
            this.audioManager.playSpellSound('fireball_impact', impactWizType);
          } else if (spellType === 'iceShard' || spellType === 'blizzard') {
            this.audioManager.playSpellSound('iceShard_impact', impactWizType);
          }
        } else {
          // Spell missed - record miss
          if (isPlayerSpell) {
            this.cpuOpponent.recordPlayerMiss();
          } else {
            this.cpuOpponent.recordCPUMiss();
          }
        }

        // Trigger visual impact effect and cleanup
        spell.triggerImpact();
        spellsToRemove.push(spell);
      }
    });

    // O(1) removal with Set
    spellsToRemove.forEach(spell => this.activeSpells.delete(spell));
  }

  private updateEffects(delta: number): void {
    if (this.postProcessing) {
      this.postProcessing.update(delta);
      const state = this.gameState.getState();
      const damageRatio = 1 - (state.player.health / state.player.maxHealth);
      this.postProcessing.setHealthVignette(damageRatio);
    }
    if (this.environmentalEffects) {
      this.environmentalEffects.update();
    }
    if (this.emoteDisplay) {
      this.emoteDisplay.update();
    }
  }

  // --- Unique wizard mechanic updates ---
  private updateUniqueWizardMechanics(delta: number): void {
    const now = Date.now();
    const state = this.gameState.getState();

    // --- Necromancer DoT: damage over time ---
    if (this.playerDotActive) {
      if (now < this.playerDotEndTime) {
        const dotDamage = this.playerDotDps * delta;
        if (dotDamage >= 1) {
          this.gameState.damagePlayer(Math.round(dotDamage));
        }
      } else {
        this.playerDotActive = false;
      }
    }
    if (this.enemyDotActive) {
      if (now < this.enemyDotEndTime) {
        const dotDamage = this.enemyDotDps * delta;
        if (dotDamage >= 1) {
          this.gameState.damageEnemy(Math.round(dotDamage));
        }
      } else {
        this.enemyDotActive = false;
      }
    }

    // --- Druid Healing Zone ---
    if (this.playerHealZoneActive) {
      if (now < this.playerHealZoneEndTime) {
        const healTick = this.playerHealZoneHps * delta;
        if (healTick >= 1) {
          const maxHp = WIZARD_CONFIGS[state.player.wizardType].health;
          const newHp = Math.min(maxHp, state.player.health + Math.round(healTick));
          this.gameState.setPlayerHealth(newHp);
        }
        // Pulse the visual
        if (this.playerHealZoneVisual) {
          const pulse = 0.8 + Math.sin(now * 0.005) * 0.2;
          (this.playerHealZoneVisual.material as THREE.MeshStandardMaterial).opacity = 0.2 * pulse;
        }
      } else {
        this.playerHealZoneActive = false;
        if (this.playerHealZoneVisual) {
          this.scene.remove(this.playerHealZoneVisual);
          this.playerHealZoneVisual.geometry.dispose();
          (this.playerHealZoneVisual.material as THREE.Material).dispose();
          this.playerHealZoneVisual = null;
        }
      }
    }
    if (this.enemyHealZoneActive) {
      if (now < this.enemyHealZoneEndTime) {
        const healTick = this.enemyHealZoneHps * delta;
        if (healTick >= 1) {
          const maxHp = WIZARD_CONFIGS[state.enemy.wizardType].health;
          const newHp = Math.min(maxHp, state.enemy.health + Math.round(healTick));
          this.gameState.setEnemyHealth(newHp);
        }
        if (this.enemyHealZoneVisual) {
          const pulse = 0.8 + Math.sin(now * 0.005) * 0.2;
          (this.enemyHealZoneVisual.material as THREE.MeshStandardMaterial).opacity = 0.2 * pulse;
        }
      } else {
        this.enemyHealZoneActive = false;
        if (this.enemyHealZoneVisual) {
          this.scene.remove(this.enemyHealZoneVisual);
          this.enemyHealZoneVisual.geometry.dispose();
          (this.enemyHealZoneVisual.material as THREE.Material).dispose();
          this.enemyHealZoneVisual = null;
        }
      }
    }

    // --- Druid Root: prevent movement ---
    if (this.playerRooted && now >= this.playerRootEndTime) {
      this.playerRooted = false;
    }
    if (this.enemyRooted && now >= this.enemyRootEndTime) {
      this.enemyRooted = false;
    }
  }

  private createHealingZone(isPlayer: boolean): void {
    const pos = isPlayer ? this.playerPosition : this.enemyPosition;

    // Create a flat glowing green disc on the arena floor
    const geometry = new THREE.CylinderGeometry(1.5, 1.5, 0.05, 16);
    const material = new THREE.MeshStandardMaterial({
      color: 0x44AA33,
      emissive: new THREE.Color(0x228822),
      emissiveIntensity: 0.5,
      transparent: true,
      opacity: 0.3,
    });
    const zone = new THREE.Mesh(geometry, material);
    zone.position.set(pos.x, 0.05, pos.z);
    this.scene.add(zone);

    if (isPlayer) {
      if (this.playerHealZoneVisual) {
        this.scene.remove(this.playerHealZoneVisual);
        this.playerHealZoneVisual.geometry.dispose();
        (this.playerHealZoneVisual.material as THREE.Material).dispose();
      }
      this.playerHealZoneVisual = zone;
      this.playerHealZoneActive = true;
      this.playerHealZoneEndTime = Date.now() + 5000; // 5 seconds
    } else {
      if (this.enemyHealZoneVisual) {
        this.scene.remove(this.enemyHealZoneVisual);
        this.enemyHealZoneVisual.geometry.dispose();
        (this.enemyHealZoneVisual.material as THREE.Material).dispose();
      }
      this.enemyHealZoneVisual = zone;
      this.enemyHealZoneActive = true;
      this.enemyHealZoneEndTime = Date.now() + 5000;
    }
  }

  private attachSpellEffects(spell: Spell): void {
    spell.setEventCallback((_spell, event, data) => {
      if (event === 'chargeStart' && this.spellEffectsManager && data) {
        const pos = data['position'] as number[];
        this.spellEffectsManager.startChargeSwirl(new THREE.Vector3(pos[0], pos[1], pos[2]), new THREE.Color(data['color'] as number));
      }
      if (event === 'chargeUpdate' && this.spellEffectsManager && data) {
        const pos = data['position'] as number[];
        this.spellEffectsManager.updateChargeSwirl(data['progress'] as number, new THREE.Vector3(pos[0], pos[1], pos[2]));
      }
      if (event === 'chargeDone' && this.spellEffectsManager) {
        this.spellEffectsManager.stopChargeSwirl();
      }
      if (event === 'impact' && data) {
        const pos = data['position'] as number[];
        const impactPos = new THREE.Vector3(pos[0], pos[1], pos[2]);
        const type = data['type'] as string;
        if (this.postProcessing) this.postProcessing.triggerImpactEffect(type === 'fireball' ? 0.01 : 0.006);
        if (this.environmentalEffects) {
          if (type === 'fireball') this.environmentalEffects.createScorchMark(impactPos);
          else if (type === 'iceShard') this.environmentalEffects.createFrostPatch(impactPos);
        }
      }
    });
  }

  private updateShields(delta: number): void {
    if (this.playerShield) {
      if (!this.playerShield.update(delta)) {
        this.playerShield = null;
        this.audioManager.play('shield_break');
      }
    }

    if (this.enemyShield) {
      if (!this.enemyShield.update(delta)) {
        this.enemyShield = null;
        this.audioManager.play('shield_break');
      }
    }

    // Update super shields
    if (this.playerSuperShield) {
      if (!this.playerSuperShield.update(delta)) {
        this.playerSuperShield = null;
        this.audioManager.play('shield_break');
      } else {
        this.playerSuperShield.setPosition(this.playerPosition);
      }
    }

    if (this.enemySuperShield) {
      if (!this.enemySuperShield.update(delta)) {
        this.enemySuperShield = null;
        this.audioManager.play('shield_break');
      } else {
        this.enemySuperShield.setPosition(this.enemyPosition);
      }
    }
  }


  private checkGameOver(): void {
    // In multiplayer, game over is handled by server
    if (this.isMultiplayer) return;

    const state = this.gameState.getState();

    if (state.screen === 'game-over') {
      this.isRunning = false;
      this.showTouchControls(false);
      this.keyboardInput.disable();
      this.uiManager.hideComboMeter();

      const playerWon = state.winner === 'player';
      this.cpuOpponent.recordGameEnd(playerWon);

      // Fire achievement match_end event
      const matchStats = this.gameState.getMatchStats();
      this.achievementManager.processEvent({
        type: 'match_end',
        data: {
          won: playerWon,
          playerHealth: state.player.health,
          enemyHealth: state.enemy.health,
          damageTaken: matchStats.totalDamageTaken,
          wizardType: state.player.wizardType,
          spellsCastThisMatch: matchStats.spellsCast,
        },
      });
      // Check cosmetic unlocks after achievements are processed
      this.cosmeticManager.checkAllUnlockConditions();

      // Trigger victory/defeat visual effects
      if (this.spellEffectsManager) {
        if (playerWon) {
          this.spellEffectsManager.createVictoryEffect(this.playerPosition);
          this.spellEffectsManager.createDefeatEffect(this.enemyPosition);
        } else {
          this.spellEffectsManager.createDefeatEffect(this.playerPosition);
          this.spellEffectsManager.createVictoryEffect(this.enemyPosition);
        }
      }
      if (this.postProcessing) {
        this.postProcessing.setBloomStrength(1.2);
      }

      // Stop theme ambient audio and play music/TTS for game over
      this.arenaThemeManager.stopAmbientAudio();
      this.ttsService.speak(GAME_PHRASES.GAME_OVER);
      if (playerWon) {
        this.audioManager.playVictoryMusic();
        setTimeout(() => {
          this.ttsService.speak(GAME_PHRASES.VICTORY);
        }, 1500);
      } else {
        this.audioManager.playDefeatMusic();
        setTimeout(() => {
          this.ttsService.speak(GAME_PHRASES.DEFEAT);
        }, 1500);
      }
    }
  }

  private gameLoopErrorCount: number = 0;
  private readonly MAX_GAME_LOOP_ERRORS = 5;

  // FPS monitoring
  private fpsCounter: HTMLElement | null = null;
  private frameCount: number = 0;
  private lastFpsUpdate: number = 0;
  private currentFps: number = 0;
  private showFps: boolean = false;

  private gameLoop(): void {
    requestAnimationFrame(() => this.gameLoop());

    try {
      const delta = this.clock.getDelta();

      if (this.arena) {
        this.arena.update(delta);
      }

      // Always update wizard animations (idle animations should play on menu too)
      this.playerWizardModel?.update(delta);
      this.enemyWizardModel?.update(delta);

      if (this.isRunning) {
        this.updateMovement(delta);
        this.updateCPU(delta);
        this.updateSpells(delta);
        this.updateShields(delta);
        this.updateCamera(delta);
        this.updateNameLabelRotations();
        this.updateEffects(delta);
        this.updateUniqueWizardMechanics(delta);
        this.checkGameOver();

        // Update dynamic audio intensity based on current health
        const currentState = this.gameState.getState();
        const playerHealthPct = currentState.player.health / currentState.player.maxHealth;
        const enemyHealthPct = currentState.enemy.health / currentState.enemy.maxHealth;
        this.audioManager.updateBattleIntensity(playerHealthPct, enemyHealthPct);

        // Update combo meter UI
        this.uiManager.updateComboMeter(currentState.playerCombo.meter);
      }

      // Use post-processing pipeline instead of direct render
      if (this.postProcessing) {
        this.postProcessing.render();
      } else {
        this.renderer.render(this.scene, this.camera);
      }

      // Update FPS counter
      this.updateFpsCounter();

      // Reset error count on successful frame
      this.gameLoopErrorCount = 0;
    } catch (error) {
      this.gameLoopErrorCount++;
      console.error('Game loop error:', error);

      // Report to client logger
      if (typeof window !== 'undefined' && (window as unknown as { reportError?: (e: Error, ctx?: Record<string, unknown>) => void }).reportError) {
        (window as unknown as { reportError: (e: Error, ctx?: Record<string, unknown>) => void }).reportError(
          error instanceof Error ? error : new Error(String(error)),
          { type: 'game_loop_error', errorCount: this.gameLoopErrorCount }
        );
      }

      // Stop game if too many consecutive errors
      if (this.gameLoopErrorCount >= this.MAX_GAME_LOOP_ERRORS) {
        console.error('Too many game loop errors, stopping game');
        this.isRunning = false;
        this.showErrorMessage('An error occurred. Please refresh the page to continue.');
      }
    }
  }

  private showErrorMessage(message: string): void {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0,0,0,0.9); display: flex; flex-direction: column;
      align-items: center; justify-content: center; z-index: 9999;
      font-family: 'Cinzel', serif; color: #C9A227;
    `;
    overlay.innerHTML = `
      <h2 style="margin-bottom: 1rem;">Something went wrong</h2>
      <p style="color: #E8DCC4; margin-bottom: 2rem;">${message}</p>
      <button onclick="location.reload()" style="
        font-family: 'Cinzel', serif; font-size: 1.1rem; padding: 1rem 2rem;
        background: linear-gradient(180deg, #2A2A2A 0%, #1A1A1A 100%);
        border: 2px solid #C9A227; color: #E8DCC4; cursor: pointer;
      ">Refresh Game</button>
    `;
    document.body.appendChild(overlay);
  }

  private updateCamera(delta: number): void {
    // Rotate camera around the arena during battle
    if (this.screenShakeIntensity > 0) {
      // Apply screen shake on top of rotation
      const shake = new THREE.Vector3(
        (Math.random() - 0.5) * this.screenShakeIntensity,
        (Math.random() - 0.5) * this.screenShakeIntensity,
        0
      );
      this.screenShakeIntensity *= 0.9;

      if (this.screenShakeIntensity < 0.01) {
        this.screenShakeIntensity = 0;
      }

      // Calculate base position from rotation
      const x = Math.sin(this.cameraAngle) * this.cameraRadius;
      const z = Math.cos(this.cameraAngle) * this.cameraRadius;
      this.camera.position.set(x + shake.x, this.cameraHeight + shake.y, z + shake.z);
    } else {
      // Smooth circular rotation
      this.cameraAngle += this.cameraRotationSpeed * delta;

      const x = Math.sin(this.cameraAngle) * this.cameraRadius;
      const z = Math.cos(this.cameraAngle) * this.cameraRadius;

      this.camera.position.set(x, this.cameraHeight, z);
    }

    // Always look at the center of the arena
    this.camera.lookAt(0, 2, 0);
  }

  private updateNameLabelRotations(): void {
    // Make name labels always face the camera
    if (this.playerNameLabel) {
      this.playerNameLabel.lookAt(this.camera.position);
    }
    if (this.enemyNameLabel) {
      this.enemyNameLabel.lookAt(this.camera.position);
    }
  }

  private onWindowResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    if (this.postProcessing) {
      this.postProcessing.onResize(window.innerWidth, window.innerHeight);
    }
  }

  // Toggle between Meshy and procedural models
  public toggleModelType(): void {
    this.useMeshyModels = !this.useMeshyModels;
    console.log(`Switched to ${this.useMeshyModels ? 'Meshy' : 'Procedural'} models`);

    // Reload wizard models with new type
    const state = this.gameState.getState();
    this.updateWizardAppearance(true, state.player.wizardType);
    this.updateWizardAppearance(false, state.enemy.wizardType);
  }

  public getUseMeshyModels(): boolean {
    return this.useMeshyModels;
  }

  // Public method to get multiplayer status
  public getIsMultiplayer(): boolean {
    return this.isMultiplayer;
  }

  public getMyRole(): PlayerRole | null {
    return this.myRole;
  }

  public getFriendClient(): FriendClient | null {
    return this.friendClient;
  }

  private getOrCreatePlayerId(): string {
    const key = 'wizard-duel-player-id';
    let id = localStorage.getItem(key);
    if (!id) {
      const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
      id = 'p_';
      for (let i = 0; i < 12; i++) {
        id += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      localStorage.setItem(key, id);
    }
    return id;
  }

  // Emote system
  public sendEmote(emoteType: EmoteType): void {
    if (!this.isMultiplayer || !this.multiplayerClient) return;

    // Client-side rate limit (5 seconds)
    const now = Date.now();
    if (now - this.lastEmoteTime < 5000) return;
    this.lastEmoteTime = now;

    this.multiplayerClient.sendEmote(emoteType);
  }

  private handleEmote(from: string, emoteType: EmoteType, player: PlayerRole): void {
    // Show floating text above the wizard
    if (this.emoteDisplay) {
      this.emoteDisplay.showEmote(emoteType, player);
    }

    // Show UI indicator
    this.uiManager.showEmoteIndicator(from, emoteType);

    // TTS voice the emote
    const ttsText = EMOTE_TTS_TEXT[emoteType];
    if (ttsText) {
      this.ttsService.speak(ttsText);
    }
  }
}
